import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { GoogleLogin } from "@react-oauth/google";
import { useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";
import RefreshIcon from "@mui/icons-material/Refresh";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import {
  adminApi,
  refreshAdminToken,
  setAdminToken,
  type ReferralAdminAmbassadorContractDetail,
  type ReferralAdminAmbassadorPostRow,
  type ReferralAdminAmbassadorRow,
  type ReferralAdminCodeDetail,
  type ReferralAdminCodeSaveParam,
  type ReferralAdminPerformanceLive,
  type ReferralAdminPerformanceMetrics,
  type ReferralAdminPerformanceOverviewResult,
  type ReferralAdminPerformanceRow,
  type ReferralAdminSettlementResult,
  type ReferralAdminSettlementRow,
  type ReferralAdminTrialMode,
  type ReferralAdminTrialUserPreview,
} from "api/adminApi";
import {
  type AmbassadorContractType,
  type AmbassadorDashboardResult,
} from "api/ambassadorApi";
import {
  PERFORMANCE_MOCK_ENABLED,
  mockPerformanceDetail,
  mockPerformanceOverview,
} from "pages/admin/performanceMock";
import UserSection from "pages/admin/user/UserSection";
import ContractDocument from "components/common/ContractDocument";
import {
  formatPhoneNumberInput,
  normalizePhoneNumber,
} from "utils/phoneNumberUtils";
import {
  CONTRACT_FILE_MAX_BYTES,
  fileNameFromContentDisposition,
  readFileAsDataUrl,
  saveBlobAsFile,
} from "utils/fileUtils";
import {
  Badge,
  Button,
  Card,
  ErrorBanner,
  Field,
  InfoRow,
  Input,
  Modal,
  NUMERIC,
  SectionHeader,
  Select,
  TD,
  TEmpty,
  TGroup,
  TH,
  THead,
  TRow,
  TSkeleton,
  Table,
  Toggle,
} from "pages/admin/ui";

type AuthPhase = "checking" | "login" | "denied" | "ready";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

// 정산 월의 상한 — 다음 달은 아직 오지 않았다.
// toISOString(UTC) 을 쓰면 KST 오전 9시 이전에 한 달 밀려 매월 1일 아침에 이번 달을 못 고른다.
const currentMonth = () => localToday().slice(0, 7);

// 정산 월의 하한 — 앰버서더 정산을 시작한 달. 그 이전은 집계할 데이터가 없다.
const SETTLEMENT_MIN_MONTH = "2026-07";

// yyyy-MM 를 delta 달만큼 옮긴다. Date 로 넘겨 연 경계를 자동으로 넘긴다.
const shiftMonth = (month: string, delta: number) => {
  const [year, m] = month.split("-").map(Number);
  const moved = new Date(year, m - 1 + delta, 1);
  return `${moved.getFullYear()}-${String(moved.getMonth() + 1).padStart(2, "0")}`;
};

// 확정일 — 결제 발생 월의 다음 달 8일에 확정하고 10일에 지급한다 (계약서 제4조 ⑤).
// 그 달 마지막 결제의 환불 창이 8일 00:00 에 닫히고, 두 시간 뒤 02:00 에 서버 크론이 확정한다.
const confirmDateOf = (month: string) => {
  const [year, m] = month.split("-").map(Number);
  if (!year || !m) return "익월 8일";
  const next = m === 12 ? { year: year + 1, month: 1 } : { year, month: m + 1 };
  return `${next.year}년 ${next.month}월 8일`;
};

// ISO 문자열 → "2026년 9월 8일 02:00". 값이 없으면 빈 문자열이라 문장이 자연스럽게 이어진다.
const formatDateTime = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// 좌측 nav 메뉴 — 디스턴싱 백오피스의 도구 단위. ready=false 는 자리만 잡아둔 준비중 메뉴.
type AdminMenu = "creator" | "user" | "coach";

const ADMIN_MENUS: { key: AdminMenu; label: string; ready: boolean }[] = [
  { key: "creator", label: "크리에이터 협업", ready: true },
  { key: "user", label: "회원관리", ready: true },
  { key: "coach", label: "코치 관리", ready: false },
];

// 크리에이터 협업 하위 메뉴 — 계약은 현재 앰버서더 계약만 다룬다.
type CreatorTab = "code" | "contract" | "performance" | "settlement" | "post";

const CREATOR_TABS: { key: CreatorTab; label: string }[] = [
  { key: "contract", label: "계약" },
  { key: "code", label: "링크 관리" },
  { key: "post", label: "게시물" },
  { key: "performance", label: "성과" },
  { key: "settlement", label: "정산" },
];

// 디스턴싱 백오피스 — @orwellhealth.org 구글 계정 중 서버 허용 목록만 접근 가능.
// 좌측 nav 로 도구를 구분한다: 크리에이터 협업(하위 메뉴: 계약·링크 관리·성과·정산) + 회원관리(준비중).
// 크리에이터 협업은 referral 전체 타입을 관리한다: affiliate(외부 명칭 "앰버서더", 정산 대상) + discount(단순할인).
// 인증은 어드민 전용 인스턴스(adminApi)가 관리 — 기존 유저/프로모션 세션과 완전 분리.
// URL 파라미터 → 탭 키. 모르는 값(오타·구버전 링크)이면 조용히 기본 탭으로 떨어진다.
const readParam = <T extends string>(
  value: string | null,
  options: { key: T }[],
  fallback: T
): T => options.find((option) => option.key === value)?.key ?? fallback;

export default function AdminPage() {
  const [phase, setPhase] = useState<AuthPhase>("checking");
  // 열려 있던 메뉴·탭은 URL 에 남긴다 — /admin 한 페이지라 상태만 들고 있으면
  // 새로고침(F5)마다 첫 탭으로 돌아간다. 링크로 특정 탭을 바로 열 수도 있다.
  const [searchParams, setSearchParams] = useSearchParams();
  const menu = readParam(searchParams.get("menu"), ADMIN_MENUS, "creator");
  const creatorTab = readParam(
    searchParams.get("tab"),
    CREATOR_TABS,
    "contract"
  );
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<ReferralAdminCodeDetail[]>([]);

  // 탭 전환은 히스토리에 쌓지 않는다 — 뒤로가기가 탭 순회가 되면 어드민을 빠져나갈 수 없다.
  const openMenu = (next: AdminMenu) =>
    setSearchParams({ menu: next, tab: creatorTab }, { replace: true });
  const openCreatorTab = (next: CreatorTab) =>
    setSearchParams({ menu, tab: next }, { replace: true });

  const enter = useCallback(async () => {
    try {
      const res = await adminApi.codeList();
      if (res.code === 0) {
        setCodes(res.result ?? []);
        setPhase("ready");
      } else {
        setError(res.message || FALLBACK_ERROR_MESSAGE);
        setPhase("login");
      }
    } catch (e) {
      const status = (e as AxiosError).response?.status;
      setPhase(status === 403 ? "denied" : "login");
    }
  }, []);

  useEffect(() => {
    (async () => {
      const token = await refreshAdminToken();
      if (!token) {
        setPhase("login");
        return;
      }
      await enter();
    })();
  }, [enter]);

  const reloadCodes = async () => {
    const res = await adminApi.codeList();
    if (res.code === 0) setCodes(res.result ?? []);
  };

  const handleGoogleLogin = async (credential?: string) => {
    if (!credential) return;
    setError(null);
    try {
      const res = await adminApi.login(credential);
      if (res.code === 0 && res.result?.accessToken) {
        setAdminToken(res.result.accessToken);
        await enter();
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    }
  };

  // 로그인 전(확인/로그인/거부)에는 nav 없이 단순 화면, 로그인 후에는 좌측 nav + 콘텐츠 레이아웃.
  if (phase !== "ready") {
    return (
      <div className="flex h-full w-full items-center justify-center overflow-y-auto bg-slate-50 px-5 py-10">
        <Card className="w-full max-w-[380px] p-8">
          <h1 className="text-lg font-semibold tracking-tight text-slate-900">
            디스턴싱 어드민
          </h1>

          {phase === "checking" && (
            <div className="mt-6 flex flex-col gap-2">
              <span className="h-9 w-full animate-pulse rounded-md bg-slate-100" />
              <span className="h-4 w-2/3 animate-pulse rounded bg-slate-100" />
            </div>
          )}

          {phase === "login" && (
            <div className="mt-6 flex flex-col items-start gap-4">
              <p className="text-sm leading-relaxed text-slate-500">
                @orwellhealth.org 구글 계정으로 로그인해 주세요.
              </p>
              <GoogleLogin
                onSuccess={(response) => handleGoogleLogin(response.credential)}
                onError={() => setError(FALLBACK_ERROR_MESSAGE)}
              />
              {error && <ErrorBanner>{error}</ErrorBanner>}
            </div>
          )}

          {phase === "denied" && (
            <p className="mt-6 text-sm leading-relaxed text-slate-500">
              권한이 없는 계정이에요. 관리자에게 어드민 등록을 요청해 주세요.
            </p>
          )}
        </Card>
      </div>
    );
  }

  const activeMenu = ADMIN_MENUS.find(({ key }) => key === menu);

  return (
    <div className="flex h-full w-full bg-slate-50">
      {/* 좌측 nav — 콘텐츠만 스크롤되고 nav 는 고정된다 */}
      <aside className="flex w-56 shrink-0 flex-col border-r border-slate-200 bg-white">
        <div className="flex h-14 items-center border-b border-slate-200 px-5">
          <span className="text-sm font-semibold tracking-tight text-slate-900">
            디스턴싱 어드민
          </span>
        </div>
        <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-3">
          {ADMIN_MENUS.map(({ key, label, ready }) => (
            <div key={key} className="flex flex-col gap-0.5">
              <button
                type="button"
                onClick={() => openMenu(key)}
                className={`flex items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors ${
                  menu === key
                    ? "bg-[#DCEFFA] font-semibold text-[#0F55A7]"
                    : "font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                {label}
                {!ready && (
                  <span className="text-[11px] font-normal text-slate-400">
                    준비중
                  </span>
                )}
              </button>
              {/* 하위 메뉴 — 상위 메뉴가 선택된 동안만 펼친다 */}
              {key === "creator" && menu === "creator" && (
                <div className="ml-3 flex flex-col gap-0.5 border-l border-slate-200 pl-2">
                  {CREATOR_TABS.map(({ key: tabKey, label: tabLabel }) => (
                    <button
                      type="button"
                      key={tabKey}
                      onClick={() => openCreatorTab(tabKey)}
                      className={`rounded-md px-3 py-1.5 text-left text-sm transition-colors ${
                        creatorTab === tabKey
                          ? "bg-slate-100 font-medium text-slate-900"
                          : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                      }`}
                    >
                      {tabLabel}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      </aside>

      <main className="flex flex-1 flex-col overflow-hidden">
        {/* 상단 바 — 현재 위치를 고정해 두고 본문만 스크롤한다 */}
        <header className="flex h-14 shrink-0 items-center border-b border-slate-200 bg-white px-8">
          <h1 className="text-sm font-semibold text-slate-900">
            {activeMenu?.label}
          </h1>
        </header>

        <div className="flex-1 overflow-y-auto">
          {/* 테이블 열이 많아(계약 11열) 좁은 폭에서는 가로 스크롤이 생긴다 — 데스크톱 어드민 기준으로 넓게 잡되,
              5K 모니터(2560px)에서 표가 화면 폭 전체로 늘어나면 행을 눈으로 따라가기 어려워 1600px 에서 멈춘다 */}
          <div className="mx-auto w-full max-w-[1600px] px-8 py-8">
            {menu === "creator" && (
              <>
                {creatorTab === "code" && (
                  <CodeSection codes={codes} onSaved={reloadCodes} />
                )}
                {creatorTab === "contract" && <AmbassadorSection />}
                {creatorTab === "performance" && <PerformanceSection />}
                {creatorTab === "settlement" && <SettlementSection />}
                {creatorTab === "post" && <AmbassadorPostSection />}
              </>
            )}

            {menu === "user" && <UserSection />}

            {/* 준비중 메뉴 공통 플레이스홀더 (회원관리·코치 관리) */}
            {!activeMenu?.ready && (
              <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-slate-300 py-20">
                <p className="text-sm font-medium text-slate-600">
                  아직 준비 중인 기능이에요
                </p>
                <p className="text-sm text-slate-400">
                  왼쪽에서 다른 메뉴를 선택해 주세요.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// ---------- 코드 관리 ----------

type CodeFormState = {
  code: string;
  name: string;
  type: string;
  isActive: boolean;
  startDate: string;
  durationDays: string;
  // 연결된 앰버서더의 계약 만료일 (없으면 빈 값) — 기간 안내에 함께 보여준다
  contractEndDate: string;
  // 수수료가 이미 정해진 링크인지 — 정해졌으면 잠근다 (아직 없으면 여기서 처음 설정할 수 있다)
  feeFixed: boolean;
  perPayerAmount: string;
};

// 계약서 제4조의 수수료 — 유효 결제 건당 5만 원. 서버 AmbassadorContractTemplates.PER_PAYER_AMOUNT 와 같은 값이고,
// 앰버서더 셀프 링크는 서버가 이 값으로 채운다. 어드민 신규 코드 폼도 같은 값에서 시작해 계약서와 갈라지지 않게 한다.
const CONTRACT_PER_PAYER_AMOUNT = 50000;

// 수수료 적용 시작월 안내 — 서버(ReferralAdminBO.upsertRate)의 규칙과 짝이다.
// 수수료를 "처음" 정하는 경우는 당월부터다 (계약 경로인 앰버서더 셀프 링크와 같은 규칙 —
// 지금까지 미산출이던 링크라 익월로 미루면 이번 달이 샌다). 화면에서 할 수 있는 건 최초 설정뿐이라
// 여기는 당월 문구만 둔다. 변경 기능을 다시 열면 그쪽은 익월 안내가 따로 필요하다.
// 각 달의 정산은 계약서 제4조 ⑤에 따라 그 다음 달에 확정·지급된다.
const feeEffectiveNotice = (isNew: boolean) => {
  const now = new Date();
  const month = (offset: number) => ((now.getMonth() + offset) % 12) + 1; // 0-based → 1-based, 12월 넘어가면 1월로
  const tail = `(${month(0)}월분은 ${month(1)}월에 정산·지급돼요.)`;
  return isNew
    ? `${month(0)}월 결제 건부터 적용돼요 — 이 링크의 첫 결제부터예요. ${tail}`
    : `${month(0)}월 결제 건부터 적용돼요. 지금까지는 수수료가 없어 미산출이었어요. ${tail}`;
};

const emptyForm: CodeFormState = {
  code: "",
  name: "",
  type: "affiliate",
  isActive: true,
  startDate: "",
  durationDays: "",
  contractEndDate: "",
  feeFixed: false,
  perPayerAmount: String(CONTRACT_PER_PAYER_AMOUNT),
};

// 만료일은 직접 입력하지 않고 시작일 + 유효 일수로 자동 계산한다 (일수 미입력 시 무기한).
const todayDate = () => new Date().toISOString().slice(0, 10);

const addDays = (date: string, days: number) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const diffDays = (start: string, end: string) =>
  Math.round(
    (new Date(end).getTime() - new Date(start).getTime()) / 86_400_000
  );

// 로컬 날짜 yyyy-MM-dd. todayDate() 는 toISOString(UTC) 이라 KST 오전 9시 이전에 하루 밀리는데,
// 성과의 "오늘"과 링크 만료 판정은 서버(LocalDate.now)와 같은 날을 가리켜야 한다.
const localToday = () => {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
};

// 만료 임박·경과 안내 — 무기한(만료일 없음)은 알릴 게 없고, 아직 여유가 있으면(3일 이상)
// 아무것도 더하지 않는다. 만료일 당일까지는 링크가 살아 있으므로 0 은 만료가 아니라 "만료일" 이다.
// 기준일에 todayDate() 를 쓰면 UTC 라 KST 오전 9시 이전에 하루 밀려 만료일 당일이
// '만료됨' 으로 뜬다 — localToday() 를 쓴다.
const EXPIRY_NOTICE_DAYS = 2;

const codeExpiryNotice = (
  endDate?: string | null
): { label: string; tone: "warn" | "idle" } | null => {
  if (!endDate) return null;
  const left = diffDays(localToday(), endDate);
  if (left < 0) return { label: "만료됨", tone: "idle" };
  if (left === 0) return { label: "만료일", tone: "warn" };
  if (left <= EXPIRY_NOTICE_DAYS)
    return { label: `만료 ${left}일 전`, tone: "warn" };
  return null;
};

// 기간 옆 만료 배지 — 만료가 코앞이거나 지난 링크에만 붙는다.
// 만료됨은 끝난 상태라 idle, 아직 살아 있는 임박 건은 손이 필요해 warn 이다.
function CodeExpiryBadge({ endDate }: { endDate?: string | null }) {
  const notice = codeExpiryNotice(endDate);
  if (!notice) return null;
  return (
    <span className="ml-2">
      <Badge tone={notice.tone}>{notice.label}</Badge>
    </span>
  );
}

// 수정 진입점 — 별도 버튼 없이 이름·코드를 눌러 연다.
// 링크처럼 보이게 두지만 실제로는 버튼이다 (이동이 아니라 폼을 연다).
function CodeEditLink({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="rounded-md text-left underline-offset-2 transition-colors hover:text-[#0F55A7] hover:underline"
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// 앰버서더는 affiliate 의 외부 명칭 — 화면 표기는 앰버서더/할인으로 통일
const typeLabel = (type: string) => (type === "discount" ? "할인" : "앰버서더");

// 앰버서더 표기 — 모집 차수를 함께 보여준다. 미지정이면 차수 없이 "앰버서더".
// 화면 어디서나 같은 모양이어야 해서 한 곳에서만 만든다: 앰버서더 1차 / 앰버서더 / 앰버서더 2차.
const ambassadorLabel = (groupNo?: number | null) =>
  groupNo == null ? "앰버서더" : `앰버서더 ${groupNoLabel(groupNo)}`;

// 유형 표기 — 할인 코드는 앰버서더 계정 개념이 없어 차수를 붙이지 않는다.
const codeTypeLabel = (detail: ReferralAdminCodeDetail) =>
  detail.type === "discount"
    ? typeLabel(detail.type)
    : ambassadorLabel(detail.ambassadorGroupNo);

// 유형 필터의 특수값 — 실제 유형 id(affiliate/discount)와 겹치지 않는다.
const TYPE_FILTER_ALL = "all";

// 상태 필터 — 링크가 지금 도는지(isActive)로만 가른다. 활성/비활성 둘 중 하나만 본다:
// 섞어 보는 화면이 없으니 목록에 상태 열을 따로 두지 않는다 (필터가 곧 그 열이다).
type StatusFilter = "active" | "inactive";

// 앰버서더는 차수까지 좁혀 본다 — 값은 "affiliate:<차수>" 형태이고, 차수 자리에 GROUP_FILTER_NONE 이면 미지정.
// ("affiliate" 단독 = 차수 무관 전체)
const AFFILIATE_GROUP_PREFIX = "affiliate:";

const matchesTypeFilter = (
  detail: ReferralAdminCodeDetail,
  typeFilter: string
) => {
  if (typeFilter === TYPE_FILTER_ALL) return true;
  if (typeFilter === "discount") return detail.type === "discount";
  if (detail.type !== "affiliate") return false;
  if (typeFilter === "affiliate") return true;
  const group = typeFilter.slice(AFFILIATE_GROUP_PREFIX.length);
  return group === GROUP_FILTER_NONE
    ? detail.ambassadorGroupNo == null
    : String(detail.ambassadorGroupNo ?? "") === group;
};

// 링크가 admin 의 입력을 기다리는 상태 — 목록에서 warn 톤을 쓰는 자리는 여기로 한정한다.
// - 활동명: 표에서 링크를 식별하는 값이라 비면 어떤 링크인지 알 수 없다.
// - 수수료: 정산 금액이 걸린 값이라 비면 정산이 잡히지 않는다.
//   단순할인은 정산 개념이 없어 원래 null 로 저장되므로(save 에서 고정) 따지지 않는다.
// 비활성 링크는 끝났거나 보류된 것이라 세지 않는다 — 다시 켜면 그때 대상이 된다.
const codeNeedsName = (detail: ReferralAdminCodeDetail) =>
  (detail.name ?? "").trim().length === 0;
const codeNeedsFee = (detail: ReferralAdminCodeDetail) =>
  detail.type === "affiliate" && detail.perPayerAmount == null;
const codeNeedsInput = (detail: ReferralAdminCodeDetail) =>
  detail.isActive && (codeNeedsName(detail) || codeNeedsFee(detail));

// 입력 대기 안내 — 대상이 있을 때만 뜨고 다 채우면 사라진다.
// 파괴적 액션이 아니라 amber(확인 필요) 톤을 쓴다 (Badge 의 warn 과 같은 계열).
// 건수만 적으면 어느 폼을 열어야 하는지 모르니 무엇이 비었는지까지 적는다.
// 한 링크에 둘 다 비어 있으면 양쪽에 잡힌다 — 배분이 아니라 "무엇이 비었나" 를 보여주는 자리다.
function CodeInputTodo({
  nameCount,
  feeCount,
  todoOnly,
  onToggle,
}: {
  nameCount: number;
  feeCount: number;
  todoOnly: boolean;
  onToggle: () => void;
}) {
  const missing = [
    nameCount > 0 ? `활동명 ${nameCount}개` : null,
    feeCount > 0 ? `수수료 ${feeCount}개` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-amber-900">
          입력이 필요한 링크가 있어요 — {missing}
        </p>
        <p className="mt-0.5 text-xs leading-relaxed text-amber-800">
          활동명이 없으면 목록에서 어떤 링크인지 알 수 없고, 수수료가 없으면
          정산 금액이 잡히지 않아요. 이름이나 코드를 눌러 채워 주세요.
        </p>
      </div>
      <Button onClick={onToggle}>
        {todoOnly ? "전체 보기" : "해당 링크만 보기"}
      </Button>
    </div>
  );
}

function CodeSection({
  codes,
  onSaved,
}: {
  codes: ReferralAdminCodeDetail[];
  onSaved: () => Promise<void>;
}) {
  // editingCode: null=폼 닫힘, ""=신규, 그 외=해당 코드 수정
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [form, setForm] = useState<CodeFormState>(emptyForm);
  // 폼을 연 시점의 스냅샷 — 바뀐 게 없으면 저장 버튼을 잠근다 (헛저장·불필요한 이력 방지)
  const [initialForm, setInitialForm] = useState<CodeFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  // 수수료를 새로 정할 때만 뜨는 확인 팝업 — 한 번 저장하면 못 바꾸고 정산 금액이 걸린 값이라 되묻는다
  const [feeConfirming, setFeeConfirming] = useState(false);
  // 검색은 코드·활동명만 본다 (표에 보이는 값). 필터는 유형(앰버서더 차수/할인)과 상태(활성/비활성).
  const [keyword, setKeyword] = useState("");
  const [typeFilter, setTypeFilter] = useState(TYPE_FILTER_ALL);
  // 기본은 활성만 — 끝난 링크가 목록을 채우면 지금 도는 링크를 찾기 어렵다.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  // 비활성화 확인 — 끄면 링크 게이트가 무효가 되므로 되묻는다 (켜는 건 묻지 않는다)
  const [deactivating, setDeactivating] = useState(false);
  // 입력 대기 링크만 보기 — 안내 배너의 버튼으로만 켠다
  const [needsInputOnly, setNeedsInputOnly] = useState(false);

  const filteredCodes = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    return codes.filter((detail) => {
      if (!matchesTypeFilter(detail, typeFilter)) return false;
      if (detail.isActive !== (statusFilter === "active")) return false;
      if (!q) return true;
      return (
        detail.code.toLowerCase().includes(q) ||
        (detail.name ?? "").toLowerCase().includes(q)
      );
    });
  }, [codes, keyword, typeFilter, statusFilter]);

  // 입력 대기 건수는 needsInputOnly 를 적용하기 전 기준으로 센다 — 토글해도 숫자가 바뀌지 않게.
  // 현재 필터 안에서 세는 것이라 비활성만 보는 중이면 대상이 0 이 되고 배너도 사라진다.
  const todoCodes = useMemo(
    () => filteredCodes.filter(codeNeedsInput),
    [filteredCodes]
  );
  const nameCount = useMemo(
    () => todoCodes.filter(codeNeedsName).length,
    [todoCodes]
  );
  const feeCount = useMemo(
    () => todoCodes.filter(codeNeedsFee).length,
    [todoCodes]
  );

  // 다 채우면 배너와 함께 필터도 저절로 풀린다 — 켜 둔 채로 대상이 0 이 되어
  // 빈 표만 남는 걸 막는다 (토글을 끄러 돌아갈 배너가 이미 사라진 뒤라서).
  const todoOnly = needsInputOnly && todoCodes.length > 0;
  const visibleCodes = todoOnly ? todoCodes : filteredCodes;

  // 위 가드는 화면만 되돌리고 토글 값은 켜진 채로 남는다 — 그대로 두면 나중에
  // 새 링크가 대상이 되는 순간 켠 적 없는 필터가 되살아난다. 값도 같이 내린다.
  useEffect(() => {
    if (todoCodes.length === 0) setNeedsInputOnly(false);
  }, [todoCodes]);

  // 차수 선택지는 실제 등록된 값에서 뽑는다 — N차 개방형이라 고정 목록을 두지 않는다.
  const affiliateGroups = useMemo(
    () =>
      Array.from(
        new Set(
          codes
            .filter((detail) => detail.type === "affiliate")
            .map((detail) => detail.ambassadorGroupNo)
            .filter((groupNo): groupNo is number => groupNo != null)
        )
      ).sort((a, b) => a - b),
    [codes]
  );

  const isNew = editingCode === "";
  // 단순할인 코드는 앰버서더 계정·정산 개념이 없다 — 관련 입력을 숨기고 null 로 저장한다
  const isDiscount = form.type === "discount";

  const openNew = () => {
    const next = { ...emptyForm, startDate: todayDate() };
    setEditingCode("");
    setForm(next);
    setInitialForm(next);
    setMessage(null);
  };

  const openEdit = (detail: ReferralAdminCodeDetail) => {
    // 만료일이 있는데 시작일이 없는 기존 코드는 시작일을 오늘로 채워 일수를 역산한다
    // (시작일 없이는 일수 계산이 불가능하고, 비워 두면 저장 시 만료일이 사라지기 때문).
    const startDate = detail.startDate ?? (detail.endDate ? todayDate() : "");
    setEditingCode(detail.code);
    const next: CodeFormState = {
      code: detail.code,
      name: detail.name ?? "",
      type: detail.type,
      isActive: detail.isActive,
      startDate,
      durationDays: detail.endDate
        ? String(diffDays(startDate, detail.endDate))
        : "",
      perPayerAmount:
        detail.perPayerAmount != null ? String(detail.perPayerAmount) : "",
      contractEndDate: detail.ambassadorContractEndDate ?? "",
      // 이미 수수료가 정해진 링크는 잠근다 — 변경은 이미 발생한 결제 건의 정산액을 흔든다.
      // 아직 없는 링크(연결만 해두고 수수료 미설정)는 여기서 처음 설정할 수 있어야 한다.
      feeFixed: detail.perPayerAmount != null,
    };
    setForm(next);
    setInitialForm(next);
    setMessage(null);
  };

  // 만료일 = 시작일 + 유효 일수 (자동 계산 — 일수를 비우면 무기한)
  const computedEndDate =
    form.startDate && form.durationDays
      ? addDays(form.startDate, Number(form.durationDays))
      : "";

  // 기간 안내 — 계산된 만료일과, 계약이 있으면 계약 만료일을 나란히 둔다.
  // 앰버서더 링크는 서버가 계약 기간으로 채워 두 값이 같지만 어드민이 바꿀 수 있다 —
  // 바꾸면 두 날짜가 갈라져 보이는 것이 곧 계약과 어긋났다는 신호다.
  const contractEndNote = form.contractEndDate
    ? `계약 만료일 ${form.contractEndDate}`
    : "";
  const durationHelper = [
    computedEndDate
      ? `만료일 ${computedEndDate}`
      : form.durationDays
        ? "시작일을 설정하면 만료일이 계산돼요"
        : "만료일 없이 무기한 활성",
    contractEndNote,
  ]
    .filter(Boolean)
    .join(" · ");

  // 폼이 열린 뒤 바뀐 게 있는가 — 없으면 저장을 잠근다.
  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initialForm),
    [form, initialForm]
  );

  // 이번 저장으로 수수료가 처음 정해지는가 — 확인 팝업을 띄울 조건이자, 안내 문구의 기준이다.
  const settingFee =
    !isDiscount && !form.feeFixed && form.perPayerAmount.trim().length > 0;

  const requestSave = () => {
    if (saving || form.code.trim().length === 0 || !dirty) return;
    if (settingFee) {
      setFeeConfirming(true);
      return;
    }
    save();
  };

  const save = async () => {
    if (saving || form.code.trim().length === 0) return;
    setFeeConfirming(false);
    setSaving(true);
    setMessage(null);
    const param: ReferralAdminCodeSaveParam = {
      code: form.code.trim(),
      name: form.name.trim() || null,
      type: form.type,
      isActive: form.isActive,
      startDate: form.startDate || null,
      endDate: computedEndDate || null,
      perPayerAmount:
        isDiscount || !form.perPayerAmount ? null : Number(form.perPayerAmount),
    };
    try {
      const res = isNew
        ? await adminApi.codeCreate(param)
        : await adminApi.codeUpdate(param);
      if (res.code === 0) {
        setEditingCode(null);
        await onSaved();
      } else {
        setMessage(res.message || FALLBACK_ERROR_MESSAGE);
      }
    } catch (e) {
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <SectionHeader title="링크 관리" count={`${visibleCodes.length}개`}>
        <Input
          value={keyword}
          placeholder="코드, 활동명 검색"
          fullWidth={false}
          className="w-56"
          onChange={(e) => setKeyword(e.target.value)}
        />
        <Select
          value={typeFilter}
          aria-label="유형 필터"
          fullWidth={false}
          className="w-40"
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value={TYPE_FILTER_ALL}>전체 유형</option>
          <option value="affiliate">앰버서더 전체</option>
          {affiliateGroups.map((groupNo) => (
            <option key={groupNo} value={`${AFFILIATE_GROUP_PREFIX}${groupNo}`}>
              앰버서더 {groupNoLabel(groupNo)}
            </option>
          ))}
          <option value={`${AFFILIATE_GROUP_PREFIX}${GROUP_FILTER_NONE}`}>
            앰버서더 미지정
          </option>
          <option value="discount">할인</option>
        </Select>
        <Select
          value={statusFilter}
          aria-label="상태 필터"
          fullWidth={false}
          className="w-28"
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
        >
          <option value="active">활성</option>
          <option value="inactive">비활성</option>
        </Select>
        <Button variant="primary" onClick={openNew}>
          새 코드 발급
        </Button>
      </SectionHeader>

      {/* 입력 대기 안내 — 대상이 없으면 아예 안 보이므로, 다 채우면 이 배너는 사라진다. */}
      {todoCodes.length > 0 && (
        <CodeInputTodo
          nameCount={nameCount}
          feeCount={feeCount}
          todoOnly={todoOnly}
          onToggle={() => setNeedsInputOnly((v) => !v)}
        />
      )}

      {editingCode !== null && (
        // 폼은 팝업으로 연다 — 표 위에 펼치면 목록이 아래로 밀려나 어느 행을 열었는지 놓친다.
        <Modal
          title={isNew ? "새 코드 발급" : "코드 수정"}
          subject={isNew ? undefined : editingCode}
          width="640px"
          onClose={() => setEditingCode(null)}
          footer={
            <>
              <Button onClick={() => setEditingCode(null)}>취소</Button>
              <Button
                variant="primary"
                disabled={form.code.trim().length === 0 || !dirty}
                loading={saving}
                loadingLabel="저장 중…"
                onClick={requestSave}
              >
                저장
              </Button>
            </>
          }
        >
          {/* 팝업 폭에서는 3열이 좁아 날짜·금액이 잘린다 — 2열로 둔다 */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="코드 (/with/코드)">
              <Input
                value={form.code}
                disabled={!isNew}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </Field>
            <Field label="표시 이름">
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label="유형">
              <Select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <option value="affiliate">앰버서더 (정산 대상)</option>
                <option value="discount">단순할인 (정산 없음)</option>
              </Select>
            </Field>
            {!isDiscount && (
              // 수수료는 한 번만 정한다 — 변경은 이미 발생한 결제 건의 정산액을 흔들어 아직 열지 않는다.
              // (서버는 익월 적용으로 기록할 준비가 되어 있다.) 아직 없는 링크는 여기서 처음 설정할 수 있다.
              <Field
                label="결제자 1인당 수수료 (원)"
                helper={
                  form.feeFixed
                    ? "이미 정해진 금액이라 바꿀 수 없어요."
                    : `계약서 기준 5만 원. ${feeEffectiveNotice(isNew)} 한 번 저장하면 바꿀 수 없어요.`
                }
              >
                <Input
                  type="number"
                  min={0}
                  disabled={form.feeFixed}
                  value={form.perPayerAmount}
                  onChange={(e) =>
                    setForm({ ...form, perPayerAmount: e.target.value })
                  }
                />
              </Field>
            )}
            <Field label="시작일">
              <Input
                type="date"
                value={form.startDate}
                onChange={(e) =>
                  setForm({ ...form, startDate: e.target.value })
                }
              />
            </Field>
            <Field label="유효 일수 (비우면 무기한)" helper={durationHelper}>
              <Input
                type="number"
                min={1}
                value={form.durationDays}
                onChange={(e) =>
                  setForm({ ...form, durationDays: e.target.value })
                }
              />
            </Field>
          </div>
          {!isNew && (
            <div className="mt-4">
              <Toggle
                checked={form.isActive}
                label="활성"
                helper={`끄면 /with/${form.code} 로 들어와도 혜택이 적용되지 않아요`}
                onChange={(next: boolean) => {
                  // 끄는 건 되묻고, 켜는 건 바로 반영한다
                  if (next) setForm({ ...form, isActive: true });
                  else setDeactivating(true);
                }}
              />
            </div>
          )}
          {message && (
            <div className="mt-4">
              <ErrorBanner>{message}</ErrorBanner>
            </div>
          )}
        </Modal>
      )}

      {/* 비활성화 확인 — 링크 게이트가 무효가 되는 변화라 되묻는다. 켤 때는 묻지 않는다. */}
      {deactivating && (
        <Modal
          title="링크를 비활성화할까요?"
          subject={form.code}
          onClose={() => setDeactivating(false)}
          footer={
            <>
              <Button onClick={() => setDeactivating(false)}>취소</Button>
              <Button
                variant="danger"
                onClick={() => {
                  setForm({ ...form, isActive: false });
                  setDeactivating(false);
                }}
              >
                비활성화
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-2 text-sm leading-relaxed text-slate-600">
            <p>
              앞으로{" "}
              <span className="font-medium text-slate-900">
                /with/{form.code}
              </span>{" "}
              로 들어와도 혜택이 적용되지 않아요.
            </p>
            <p className="text-slate-500">
              이미 유입된 회원의 기여와 정산은 그대로예요. 다시 켜면 원래대로
              돌아와요.
            </p>
          </div>
        </Modal>
      )}

      {/* 수수료 확인 — 정산 금액이 걸린 값이고 저장하면 되돌릴 수 없어 한 번 되묻는다.
          언제부터 적용되는지를 예시와 함께 보여준다 (서버 upsertRate 규칙과 같은 문구). */}
      {feeConfirming && (
        <Modal
          title="수수료를 이 금액으로 정할까요?"
          subject={form.code.trim()}
          onClose={() => setFeeConfirming(false)}
          footer={
            <>
              <Button onClick={() => setFeeConfirming(false)}>취소</Button>
              <Button
                variant="primary"
                loading={saving}
                loadingLabel="저장 중…"
                onClick={save}
              >
                이 금액으로 저장
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <p className="text-2xl font-semibold tabular-nums text-slate-900">
              {Number(form.perPayerAmount).toLocaleString()}원
              <span className="ml-2 text-sm font-normal text-slate-500">
                유효 결제 1건당
              </span>
            </p>
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm leading-relaxed text-slate-600">
              {feeEffectiveNotice(isNew)}
            </div>
            <p className="text-sm text-slate-500">
              저장하면 이 링크의 수수료는 바뀌지 않아요. 금액을 잘못 넣었다면
              지금 취소하고 고쳐 주세요.
            </p>
          </div>
        </Modal>
      )}

      {/* 목록은 코드를 식별하고 상태를 훑는 용도로만 둔다 — 차수·전화번호·수수료·계정 상태는
          수정 폼에서 보고 고친다. 수정은 별도 버튼 없이 이름·코드를 눌러 연다 */}
      <Table>
        <THead>
          <TH>이름</TH>
          <TH>코드</TH>
          <TH>유형</TH>
          <TH>기간</TH>
        </THead>
        <tbody>
          {visibleCodes.length === 0 && (
            <TEmpty colSpan={4}>
              {codes.length === 0
                ? "등록된 코드가 없어요"
                : "조건에 맞는 코드가 없어요"}
            </TEmpty>
          )}
          {visibleCodes.map((detail) => (
            <TRow
              key={detail.code}
              className={codeNeedsInput(detail) ? "bg-amber-50/60" : ""}
            >
              <TD>
                <CodeEditLink onClick={() => openEdit(detail)}>
                  {detail.name ?? "-"}
                </CodeEditLink>
              </TD>
              <TD className="font-medium">
                <CodeEditLink onClick={() => openEdit(detail)}>
                  {detail.code}
                </CodeEditLink>
              </TD>
              <TD muted>{codeTypeLabel(detail)}</TD>
              <TD muted>
                {detail.startDate ?? "…"} ~ {detail.endDate ?? "무기한"}
                <CodeExpiryBadge endDate={detail.endDate} />
              </TD>
            </TRow>
          ))}
        </tbody>
      </Table>
    </section>
  );
}

// ---------- 앰버서더 계정·계약 정보 ----------

const PAGE_SIZE = 100;

// 차수 필터의 특수값 — 실제 차수는 숫자 문자열("1", "2", …)이라 겹치지 않는다.
const GROUP_FILTER_ALL = "all";
const GROUP_FILTER_NONE = "none";

// 내부자(운영·임직원) 표시용 차수 값. 모집 차수가 아니라 분류라 별도 컬럼을 두지 않고
// 음수 자리를 쓴다 — DB 는 그대로 정수이고 화면 표기만 다르다.
const INSIDER_GROUP_NO = -1;

const groupNoLabel = (groupNo: number) =>
  groupNo === INSIDER_GROUP_NO ? "내부자" : `${groupNo}차`;

// 저장을 허용하는 값 — 1 이상의 차수 또는 내부자(-1). 0 과 그 밖의 음수는 없다.
const isValidGroupNo = (groupNo: number) =>
  groupNo === INSIDER_GROUP_NO || groupNo >= 1;

// admin 이 움직여야 하는 상태 판정 — 목록에서 warn 톤을 쓰는 자리는 여기로 한정한다.
// 완료된 값(계약 완료·서류 제출·체결 일시)은 확인용이라 idle 로 내리고, 미체결·서류 대기는
// 앰버서더가 움직일 차례라 대기로 세지 않는다.
// - 계약 승인 대기: 서류까지 들어왔는데 승인이 안 돼 앰버서더가 링크를 못 만드는 상태
// - 체험 승인 대기: 체험을 신청했는데 계정 연동이 안 된 상태
const needsContractComplete = (row: ReferralAdminAmbassadorRow) =>
  !!row.documentsSubmitted && !row.contractCompleted;
const needsTrialCheck = (row: ReferralAdminAmbassadorRow) =>
  !row.trialUserId && !!row.trialRequestUserId;
const needsAdminAction = (row: ReferralAdminAmbassadorRow) =>
  needsContractComplete(row) || needsTrialCheck(row);

// 열 묶음 경계 — 묶음의 첫 열에 붙여 헤더의 TGroup 선과 세로로 잇는다.
const GROUP_EDGE = "border-l border-slate-200";

// 모집 차수 셀 — 지정된 차수는 텍스트로 접어 두고 더블 클릭했을 때만 입력으로 바뀐다 (표를 조용하게 유지).
// 미지정은 admin 이 채워야 하는 값이라 입력창을 바로 노출한다.
// 저장은 포커스를 벗어나거나 Enter 를 누를 때. 비우면 미지정(null)으로 되돌린다.
function GroupCell({
  row,
  onSaved,
}: {
  row: ReferralAdminAmbassadorRow;
  onSaved: (groupNo: number | null) => void;
}) {
  const saved = row.groupNo ?? null;
  const [value, setValue] = useState(saved === null ? "" : String(saved));
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  // 편집 상태 — 지정된 차수만 접어 두므로 미지정(saved === null)에는 관여하지 않는다
  const [editing, setEditing] = useState(false);

  // 목록이 갱신되면(저장 성공·재조회) 입력값도 서버 값에 맞춘다
  useEffect(() => {
    setValue(saved === null ? "" : String(saved));
  }, [saved]);

  const save = async () => {
    const raw = value.trim();
    // 부호를 살려 읽는다 — 내부자(-1)를 입력할 수 있어야 한다.
    // 빈 입력만 미지정(null)으로 되돌리고, 숫자로 읽히지 않으면 저장하지 않는다
    // (오타를 숫자만 뽑아 저장하거나 조용히 지우지 않는다).
    const next =
      raw === "" ? null : /^-?\d+$/.test(raw) ? Number(raw) : undefined;
    // 허용값이 아니면 되돌린다 (0 차는 없고, -1 외의 음수도 없다)
    if (next === undefined || (next !== null && !isValidGroupNo(next))) {
      setValue(saved === null ? "" : String(saved));
      setEditing(false);
      return;
    }
    if (next === saved) {
      setValue(next === null ? "" : String(next));
      setEditing(false);
      return;
    }
    setSaving(true);
    setFailed(false);
    try {
      const res = await adminApi.ambassadorGroup(row.ambassadorId, next);
      if (res.code === 0) {
        onSaved(next);
        setEditing(false);
      } else {
        setFailed(true);
        setValue(saved === null ? "" : String(saved));
      }
    } catch (e) {
      setFailed(true);
      setValue(saved === null ? "" : String(saved));
    } finally {
      setSaving(false);
    }
  };

  // 지정된 차수 — 평소엔 텍스트로 두고 더블 클릭해야 편집으로 들어간다 (한 번 눌러서는 바뀌지 않는다)
  if (saved !== null && !editing) {
    return (
      <button
        type="button"
        aria-label="모집 차수 수정"
        title="더블 클릭해 수정"
        onDoubleClick={() => setEditing(true)}
        // 마우스 없이도 들어갈 수 있게 — onClick 을 비운 자리를 Enter 로 메운다
        onKeyDown={(e) => {
          if (e.key === "Enter") setEditing(true);
        }}
        // 더블 클릭할 때 텍스트가 선택되며 깜빡이지 않게 한다
        className="select-none rounded-md px-1 py-0.5 text-sm text-slate-900 hover:bg-slate-100"
      >
        {groupNoLabel(saved)}
      </button>
    );
  }

  return (
    <span className="flex items-center gap-1">
      <Input
        value={value}
        invalid={failed}
        inputMode="numeric"
        maxLength={3}
        disabled={saving}
        placeholder="-"
        aria-label="모집 차수"
        sizing="sm"
        fullWidth={false}
        className="w-12 text-center"
        // 편집으로 들어온 경우에만 포커스를 가져간다 — 미지정 행은 목록 렌더에서 포커스를 뺏으면 안 된다
        autoFocus={editing}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
      />
      <span className="text-slate-400">차</span>
    </span>
  );
}

const contractTypeLabel = (type?: string | null) => {
  if (type === "freelancer") return "프리랜서";
  if (type === "business_general") return "사업자(일반)";
  if (type === "business_simplified") return "사업자(간이)";
  return "-";
};

// data URL 의 MIME 으로 다운로드 파일 확장자를 정한다 (예: data:application/pdf;... → pdf)
const fileExtOf = (dataUrl: string) => {
  const mime = dataUrl.slice(5, dataUrl.indexOf(";"));
  if (mime === "application/pdf") return "pdf";
  return mime.split("/")[1] || "bin";
};

// 전자서명 체결 시각(ISO) → "2026-08-13 14:05" 표기
const formatSignedAtText = (signedAt: string) =>
  signedAt.replace("T", " ").slice(0, 16);

// 주민등록번호(숫자 13자리) → 000000-0000000 표기
const formatRrn = (rrn: string) =>
  rrn.length === 13 ? `${rrn.slice(0, 6)}-${rrn.slice(6)}` : rrn;

// 서버 ProgressStatusConstants / SubscriptionStatusConstants 의 id → 표시 라벨
const progressStatusLabel = (status?: string | null) => {
  if (status === "preparing") return "준비중";
  if (status === "in_progress") return "진행중";
  if (status === "terminated") return "종료";
  if (status === "recommended_stop") return "중단 권고";
  if (status === "leave") return "탈퇴";
  if (status === "auto_stop") return "자동 중단";
  return status ?? "-";
};

// 체험 설정 적용 방식 라벨 — 서버가 미리보기에서 판별해 준 mode 를 그대로 보여준다
const trialModeLabel = (mode: ReferralAdminTrialMode, extendDays?: number) => {
  if (mode === "init") return "신규 체험 시작 (100일)";
  if (mode === "extend") return `이용 중, 만료일 ${extendDays ?? 100}일 연장`;
  return "재구독, 오늘부터 100일 재개";
};

// 가입 방식 라벨 — 서버 oauthIssuer 값 (kakao/google/apple/firebase_email/web 등)
const oauthIssuerLabel = (issuer?: string | null) => {
  if (!issuer) return "-";
  if (issuer === "kakao") return "카카오";
  if (issuer === "google") return "구글";
  if (issuer === "apple") return "애플";
  if (issuer === "firebase_email") return "이메일";
  if (issuer === "web") return "웹";
  return issuer;
};

// 계약 대상 표시명 — 활동명이 없으면 전화번호, 그것도 없으면 내부 id 로 떨어진다
// 다운로드 파일명의 기준 이름 — 활동명, 없으면 계정 id.
// 전화번호는 쓰지 않는다: 받은 파일을 메일·드라이브로 넘기면 파일명만으로 개인정보가 따라간다.
const fileOwnerName = (row: { name?: string | null; ambassadorId: number }) =>
  row.name?.trim() || String(row.ambassadorId);

// 계약 명의 — 프리랜서는 실명, 사업자는 법인(사업자)명.
// 형태가 아직 없거나(계약 미제출) 기대한 쪽이 비어 있으면 남은 쪽을 보여준다.
const contractLegalName = (row: ReferralAdminAmbassadorRow) =>
  (row.contractType === "freelancer"
    ? row.realName || row.businessName
    : row.businessName || row.realName) || "-";

// 아래 라벨 함수들은 셀 렌더와 정렬이 같은 값을 쓰게 하려고 뽑아 둔 것이다.
// 정렬 기준은 "화면에 보이는 값" — 셀과 정렬이 갈라지면 정렬 결과를 설명할 수 없다.
// 값이 없는 칸은 null 을 돌려주고, 정렬은 null 을 방향과 무관하게 뒤로 보낸다.

// 계약 상태 — 계약 행이 없으면 미체결, 있으면 어드민 승인 여부.
// 대기 쪽을 "승인 대기"로 부르면 끝난 쪽은 "완료"만으로 뜻이 선다 (승인 완료의 줄임).
// 예전 "완료 처리"는 계약이 끝났다는 뜻으로 읽혀 쓰지 않는다 — 이 동작은 계약의 시작을 여는 것이다.
const contractStatusLabel = (row: ReferralAdminAmbassadorRow) =>
  !row.contractSubmitted
    ? "미체결"
    : row.contractCompleted
      ? "완료"
      : "승인 대기";

// 체결 방식 — 앰버서더 페이지에서 서명했으면 체결일, 아니면 외부 전자서명으로 체결한 건이다.
// 계약 행이 없으면 체결 자체가 없어 보여줄 게 없다 (상태의 "미체결"이 그 자리를 말한다).
const signingLabel = (row: ReferralAdminAmbassadorRow) =>
  !row.contractSubmitted
    ? null
    : row.signedAt
      ? formatSignedAtText(row.signedAt).slice(0, 10)
      : "외부";

const documentsLabel = (row: ReferralAdminAmbassadorRow) =>
  row.documentsSubmitted ? "제출" : "미제출";

// 체험 계정 — 상태가 있으면 배지, 아직 없으면 액션 버튼으로 렌더한다 (라벨은 공통).
const trialLabel = (row: ReferralAdminAmbassadorRow) =>
  row.trialUserId
    ? row.trialUserSubscriptionStatus === "subscribing"
      ? "설정 완료"
      : "종료"
    : row.trialRequestUserId
      ? "승인 대기"
      : "설정";

type SortKey =
  | "groupNo"
  | "name"
  | "codes"
  | "legalName"
  | "phoneNumber"
  | "contractType"
  | "contractStatus"
  | "signing"
  | "documents"
  | "trial"
  | "signedUpDate";

type SortState = { key: SortKey; dir: "asc" | "desc" } | null;

const SORT_VALUE: Record<
  SortKey,
  (row: ReferralAdminAmbassadorRow) => string | number | null
> = {
  groupNo: (r) => r.groupNo ?? null,
  name: (r) => r.name || null,
  codes: (r) => r.codes[0] || null,
  legalName: (r) => {
    const value = contractLegalName(r);
    return value === "-" ? null : value;
  },
  phoneNumber: (r) => r.phoneNumber || null,
  contractType: (r) =>
    r.contractType ? contractTypeLabel(r.contractType) : null,
  // 계약 열은 상태(배지)와 체결 정보(보조 텍스트)를 함께 보여준다 —
  // 상태로 묶고 그 안에서 체결일 순으로 서게 이어 붙인다.
  contractStatus: (r) => `${contractStatusLabel(r)} ${signingLabel(r) ?? ""}`,
  // 같은 열의 두 번째 정렬 — 체결일만으로 세운다. 날짜 → "외부"(외부 체결) → 미체결 순.
  signing: (r) => signingLabel(r),
  documents: (r) => documentsLabel(r),
  trial: (r) => trialLabel(r),
  signedUpDate: (r) => r.signedUpDate || null,
};

// 빈 값(null)은 오름·내림 어느 쪽에서도 뒤로 — 정렬했더니 "-" 가 위를 채우면 목록이 쓸모없어진다.
const compareSortValue = (
  a: string | number | null,
  b: string | number | null
) => {
  if (a === null) return b === null ? 0 : 1;
  if (b === null) return -1;
  return typeof a === "number" && typeof b === "number"
    ? a - b
    : String(a).localeCompare(String(b), "ko");
};

// 정렬 버튼 — 누르면 오름차순 → 내림차순 → 해제로 돈다.
// 정렬 중이 아닌 것도 조작 가능함을 알 수 있게 흐린 표식을 항상 둔다.
// 한 열이 값을 둘 보여주면(계약: 상태 + 체결일) 헤더 하나에 버튼을 둘 둔다.
function SortButton<K extends string>({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: K;
  sort: { key: K; dir: "asc" | "desc" } | null;
  onSort: (key: K) => void;
}) {
  const active = sort?.key === sortKey;
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 transition-colors hover:text-slate-900"
      onClick={() => onSort(sortKey)}
    >
      {label}
      <span className={active ? "text-[#0F55A7]" : "text-slate-300"}>
        {active ? (sort?.dir === "asc" ? "↑" : "↓") : "↕"}
      </span>
    </button>
  );
}

// 정렬 하나짜리 열 제목
function SortTH<K extends string>({
  label,
  sortKey,
  sort,
  onSort,
  className = "",
}: {
  label: string;
  sortKey: K;
  sort: { key: K; dir: "asc" | "desc" } | null;
  onSort: (key: K) => void;
  className?: string;
}) {
  return (
    <TH className={className}>
      <SortButton label={label} sortKey={sortKey} sort={sort} onSort={onSort} />
    </TH>
  );
}

const ambassadorSubject = (row: ReferralAdminAmbassadorRow) =>
  row.name ??
  (row.phoneNumber
    ? formatPhoneNumberInput(row.phoneNumber)
    : String(row.ambassadorId));

// 앰버서더 계정 목록 + 계약 정보 상세(파일 다운로드) — 계약서 발송 실무용.
// 목록은 파일 제외 요약이고, 파일(base64)은 행을 눌렀을 때만 상세 API 로 로드한다.
function AmbassadorSection() {
  const [rows, setRows] = useState<ReferralAdminAmbassadorRow[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [detail, setDetail] =
    useState<ReferralAdminAmbassadorContractDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // 목록은 전체를 한 번에 받아오므로 검색·차수 필터·페이징은 모두 클라이언트에서 처리한다.
  const [keyword, setKeyword] = useState("");
  const [groupFilter, setGroupFilter] = useState<string>(GROUP_FILTER_ALL);
  // 처리 대기만 보기 — 목록이 페이지를 넘기면 색만으로는 뒤 페이지의 대기 건을 놓친다
  const [actionOnly, setActionOnly] = useState(false);
  // 열 제목 정렬 — 누를 때마다 오름차순 → 내림차순 → 해제(서버 순서)로 돈다
  const [sort, setSort] = useState<SortState>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminApi.ambassadorList();
        if (res.code === 0) setRows(res.result ?? []);
        else setMessage(res.message || FALLBACK_ERROR_MESSAGE);
      } catch (e) {
        setMessage(FALLBACK_ERROR_MESSAGE);
      } finally {
        setListLoading(false);
      }
    })();
  }, []);

  // 차수 선택지는 실제 등록된 값에서 뽑는다 — N차 개방형이라 고정 목록을 두지 않는다.
  const groupOptions = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .map((r) => r.groupNo)
            .filter((g): g is number => g !== null && g !== undefined)
        )
      ).sort((a, b) => a - b),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    // 전화번호는 하이픈 유무와 무관하게 숫자만으로 비교한다. 단 숫자·하이픈만 입력했을 때로 한정한다 —
    // "code7" 같은 질의에서 섞인 숫자가 남의 번호에 걸리면 검색 결과가 엉뚱하게 늘어난다.
    const qDigits = /^[0-9\s-]+$/.test(q) ? q.replace(/[^0-9]/g, "") : "";
    return rows.filter((row) => {
      const groupMatched =
        groupFilter === GROUP_FILTER_ALL ||
        (groupFilter === GROUP_FILTER_NONE
          ? row.groupNo === null || row.groupNo === undefined
          : String(row.groupNo ?? "") === groupFilter);
      if (!groupMatched) return false;
      if (!q) return true;
      return (
        (row.name ?? "").toLowerCase().includes(q) ||
        // 계약 명의 — 형태와 무관하게 둘 다 본다 (형태가 바뀐 건도 걸리게)
        (row.realName ?? "").toLowerCase().includes(q) ||
        (row.businessName ?? "").toLowerCase().includes(q) ||
        row.codes.some((code) => code.toLowerCase().includes(q)) ||
        (qDigits !== "" && (row.phoneNumber ?? "").includes(qDigits))
      );
    });
  }, [rows, keyword, groupFilter]);

  // 대기 건수는 actionOnly 를 적용하기 전 기준으로 센다 — 토글해도 숫자가 바뀌지 않게.
  const actionCount = useMemo(
    () => filteredRows.filter(needsAdminAction).length,
    [filteredRows]
  );
  const visibleRows = useMemo(
    () => (actionOnly ? filteredRows.filter(needsAdminAction) : filteredRows),
    [filteredRows, actionOnly]
  );

  const sortedRows = useMemo(() => {
    if (!sort) return visibleRows;
    const value = SORT_VALUE[sort.key];
    const sign = sort.dir === "asc" ? 1 : -1;
    // null 은 compareSortValue 가 항상 뒤로 보내므로 방향(sign)을 곱하지 않는다
    return [...visibleRows].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (av === null || bv === null) return compareSortValue(av, bv);
      return compareSortValue(av, bv) * sign;
    });
  }, [visibleRows, sort]);

  const toggleSort = (key: SortKey) => {
    setSort((prev) =>
      prev?.key !== key
        ? { key, dir: "asc" }
        : prev.dir === "asc"
          ? { key, dir: "desc" }
          : null
    );
    setPage(1);
  };

  // 필터·검색으로 결과가 줄면 현재 페이지가 범위를 벗어날 수 있어 표시 시점에 보정한다.
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = sortedRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // 차수 저장 성공 시 목록 행을 갱신한다 (재조회 없이 필터·선택지에 즉시 반영).
  const applyGroupNo = (ambassadorId: number, groupNo: number | null) =>
    setRows((prev) =>
      prev.map((r) => (r.ambassadorId === ambassadorId ? { ...r, groupNo } : r))
    );

  const openDetail = async (ambassadorId: number) => {
    if (detailLoading) return;
    setDetailLoading(true);
    setMessage(null);
    try {
      const res = await adminApi.ambassadorContract(ambassadorId);
      if (res.code === 0 && res.result) setDetail(res.result);
      else setMessage(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setDetailLoading(false);
    }
  };

  // 계약 정보 수기 입력 팝업 — 앰버서더가 직접 내지 않은 건(운영이 서류를 따로 받은 건)을
  // 운영이 대신 채운다. 계약형태·당사자 정보·통장사본·사업자등록증이 대상이고,
  // 정산 정보(주민번호·계좌)는 앰버서더 제출 경로에만 둔다.
  const [infoTarget, setInfoTarget] =
    useState<ReferralAdminAmbassadorRow | null>(null);
  const [infoType, setInfoType] =
    useState<AmbassadorContractType>("freelancer");
  const [infoRealName, setInfoRealName] = useState("");
  const [infoBusinessName, setInfoBusinessName] = useState("");
  const [infoEmail, setInfoEmail] = useState("");
  const [infoPhoneNumber, setInfoPhoneNumber] = useState("");
  const [infoBankbook, setInfoBankbook] = useState<string | null>(null);
  const [infoBankbookName, setInfoBankbookName] = useState<string | null>(null);
  const [infoBusinessReg, setInfoBusinessReg] = useState<string | null>(null);
  const [infoBusinessRegName, setInfoBusinessRegName] = useState<string | null>(
    null
  );
  // 열었을 때의 값 — 저장 버튼 활성화(변경 여부) 판정 기준.
  const [infoInitial, setInfoInitial] = useState<{
    type: AmbassadorContractType;
    realName: string;
    businessName: string;
    email: string;
    phoneNumber: string;
  } | null>(null);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [infoSubmitting, setInfoSubmitting] = useState(false);
  // 이미 올라간 서류 — 목록 행에는 파일이 없어(용량) 팝업을 열 때 상세로 불러온다.
  const [infoDetail, setInfoDetail] =
    useState<ReferralAdminAmbassadorContractDetail | null>(null);
  const [infoLoading, setInfoLoading] = useState(false);

  const infoIsBusiness = infoType !== "freelancer";

  const openContractInfo = async (row: ReferralAdminAmbassadorRow) => {
    setInfoTarget(row);
    setInfoBankbook(null);
    setInfoBankbookName(null);
    setInfoBusinessReg(null);
    setInfoBusinessRegName(null);
    setInfoError(null);
    setInfoDetail(null);
    setInfoInitial(null);
    // 계약 행이 없는 건(미제출)은 상세가 비어 온다 — 그대로 빈 폼으로 시작한다.
    let next = {
      type: (row.contractType as AmbassadorContractType) ?? "freelancer",
      realName: "",
      businessName: "",
      email: "",
      phoneNumber: "",
    };
    setInfoLoading(true);
    try {
      const res = await adminApi.ambassadorContract(row.ambassadorId);
      if (res.code === 0 && res.result) {
        const d = res.result;
        setInfoDetail(d);
        next = {
          type: (d.contractType as AmbassadorContractType) ?? next.type,
          realName: d.realName ?? "",
          businessName: d.businessName ?? "",
          email: d.contractEmail ?? "",
          phoneNumber: d.contractPhoneNumber
            ? formatPhoneNumberInput(d.contractPhoneNumber)
            : "",
        };
      }
    } catch (e) {
      // 무시 — 빈 폼으로 입력하면 된다
    } finally {
      // 폼 값과 스냅샷을 같은 값으로 함께 세운다 — 어긋나면 열자마자 "수정됨"이 된다
      setInfoType(next.type);
      setInfoRealName(next.realName);
      setInfoBusinessName(next.businessName);
      setInfoEmail(next.email);
      setInfoPhoneNumber(next.phoneNumber);
      setInfoInitial(next);
      setInfoLoading(false);
    }
  };

  // 저장할 게 있는지 — 바뀐 값이 없으면 저장 버튼을 잠근다.
  // 계약 행이 아직 없는 건은 저장 자체가 생성이라 항상 열어 둔다.
  const infoDirty = useMemo(() => {
    if (infoTarget && !infoTarget.contractSubmitted) return true;
    if (infoBankbook || infoBusinessReg) return true;
    if (!infoInitial) return false;
    return (
      infoType !== infoInitial.type ||
      infoRealName.trim() !== infoInitial.realName.trim() ||
      infoBusinessName.trim() !== infoInitial.businessName.trim() ||
      infoEmail.trim() !== infoInitial.email.trim() ||
      normalizePhoneNumber(infoPhoneNumber) !==
        normalizePhoneNumber(infoInitial.phoneNumber)
    );
  }, [
    infoTarget,
    infoInitial,
    infoBankbook,
    infoBusinessReg,
    infoType,
    infoRealName,
    infoBusinessName,
    infoEmail,
    infoPhoneNumber,
  ]);

  // 서류 선택 — 서버와 같은 상한(5MB)·형식을 클릭·드롭 공통으로 검사한다.
  const handleInfoFile = async (
    file: File | undefined,
    setData: (v: string | null) => void,
    setName: (v: string | null) => void
  ) => {
    setInfoError(null);
    if (!file) return;
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      setInfoError("이미지 또는 PDF 파일만 올릴 수 있어요.");
      return;
    }
    if (file.size > CONTRACT_FILE_MAX_BYTES) {
      setInfoError("파일 크기는 5MB 이하로 올려 주세요.");
      return;
    }
    setData(await readFileAsDataUrl(file));
    setName(file.name);
  };

  const submitContractInfo = async () => {
    if (!infoTarget || infoSubmitting) return;
    setInfoSubmitting(true);
    setInfoError(null);
    try {
      const res = await adminApi.ambassadorContractInfo({
        ambassadorId: infoTarget.ambassadorId,
        type: infoType,
        // 빈 문자열은 보내지 않는다 — 서버에서 "안 바꿈"과 "빈 값으로 바꿈"을 가르는 기준이다
        realName: infoRealName.trim() || undefined,
        businessName: infoBusinessName.trim() || undefined,
        email: infoEmail.trim() || undefined,
        phoneNumber: normalizePhoneNumber(infoPhoneNumber) || undefined,
        bankbookFile: infoBankbook ?? undefined,
        bankbookFileName: infoBankbookName ?? undefined,
        businessRegistrationFile: infoBusinessReg ?? undefined,
        businessRegistrationFileName: infoBusinessRegName ?? undefined,
      });
      if (res.code !== 0) {
        setInfoError(res.message || FALLBACK_ERROR_MESSAGE);
        return;
      }
      // 목록에 즉시 반영 — 계약형태·제출 여부·계약 행 존재가 이 팝업으로 바뀐다.
      setRows((prev) =>
        prev.map((r) =>
          r.ambassadorId === infoTarget.ambassadorId
            ? {
                ...r,
                contractType: infoType,
                contractSubmitted: true,
                documentsSubmitted:
                  r.documentsSubmitted || infoBankbook !== null,
              }
            : r
        )
      );
      setInfoTarget(null);
    } catch (e) {
      setInfoError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setInfoSubmitting(false);
    }
  };

  // 계약 승인 팝업 상태 — 승인할 때, 그리고 승인 뒤에도 체결된 계약서 사본을 업로드·교체할 수 있다.
  const [completing, setCompleting] =
    useState<ReferralAdminAmbassadorRow | null>(null);
  const [completeFile, setCompleteFile] = useState<string | null>(null);
  const [completeFileName, setCompleteFileName] = useState<string | null>(null);
  const [completeError, setCompleteError] = useState<string | null>(null);
  const [completeSubmitting, setCompleteSubmitting] = useState(false);
  const [completeDragging, setCompleteDragging] = useState(false);
  // 보관된 사본 — 목록 행에는 파일이 없어(용량) 팝업을 열 때 상세로 따로 불러온다.
  const [completeExisting, setCompleteExisting] = useState<string | null>(null);
  const [completeExistingName, setCompleteExistingName] = useState<
    string | null
  >(null);
  const [completeExistingLoading, setCompleteExistingLoading] = useState(false);

  // 완료/해제 API 호출 — 성공 시 null, 실패 시 오류 메시지를 반환한다 (호출처가 표시 위치를 정한다).
  const applyContractComplete = async (
    row: ReferralAdminAmbassadorRow,
    completed: boolean,
    contractFile: string | null,
    contractFileName?: string | null
  ): Promise<string | null> => {
    try {
      const res = await adminApi.ambassadorContractComplete(
        row.ambassadorId,
        completed,
        contractFile,
        contractFileName
      );
      if (res.code === 0) {
        setRows((prev) =>
          prev.map((r) =>
            r.ambassadorId === row.ambassadorId
              ? { ...r, contractCompleted: completed }
              : r
          )
        );
        return null;
      }
      return res.message || FALLBACK_ERROR_MESSAGE;
    } catch (e) {
      return FALLBACK_ERROR_MESSAGE;
    }
  };

  // 계약 승인 토글 — 승인 전이면 사본 업로드와 함께 승인하고, 승인 후에는 같은 팝업에서
  // 사본만 교체하거나 승인을 취소한다 (사본 보관을 위해 취소 → 재승인을 거치지 않게 한다).
  const toggleContractComplete = (row: ReferralAdminAmbassadorRow) => {
    setCompleting(row);
    setCompleteFile(null);
    setCompleteFileName(null);
    setCompleteError(null);
    loadExistingContractFile(row.ambassadorId);
  };

  // 보관된 사본 조회 — 교체 전에 무엇이 올라가 있는지 확인·다운로드할 수 있게 한다.
  // 실패해도 업로드 자체는 막지 않는다 (기존 사본만 안 보인다).
  const loadExistingContractFile = async (ambassadorId: number) => {
    setCompleteExisting(null);
    setCompleteExistingName(null);
    setCompleteExistingLoading(true);
    try {
      const res = await adminApi.ambassadorContract(ambassadorId);
      if (res.code === 0 && res.result) {
        setCompleteExisting(res.result.contractFile ?? null);
        setCompleteExistingName(res.result.contractFileName ?? null);
      }
    } catch (e) {
      // 무시 — 아래 렌더가 "없음"으로 표시된다
    } finally {
      setCompleteExistingLoading(false);
    }
  };

  // 승인 취소 — 보관된 사본은 서버에서 유지된다.
  const releaseComplete = async () => {
    if (!completing || completeSubmitting) return;
    if (
      !window.confirm("계약 승인을 취소할까요? 보관된 계약서 사본은 유지돼요.")
    ) {
      return;
    }
    setCompleteSubmitting(true);
    const error = await applyContractComplete(completing, false, null);
    setCompleteSubmitting(false);
    if (error) {
      setCompleteError(error);
      return;
    }
    setCompleting(null);
  };

  // 팝업의 파일 선택 — 서버와 동일 상한(5MB), base64 data URL 로 읽어 보관한다.
  // 드롭된 파일은 input accept 필터를 안 거치므로 클릭·드롭 공통으로 형식을 검사한다.
  const handleCompleteFile = async (file?: File) => {
    setCompleteError(null);
    if (!file) return;
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      setCompleteError("이미지 또는 PDF 파일만 올릴 수 있어요.");
      return;
    }
    if (file.size > CONTRACT_FILE_MAX_BYTES) {
      setCompleteError("파일 크기는 5MB 이하로 올려 주세요.");
      return;
    }
    setCompleteFile(await readFileAsDataUrl(file));
    setCompleteFileName(file.name);
  };

  // 승인 / 사본 저장 — 승인 상태에서 호출하면 사본만 교체된다 (서버가 최초 승인 시각을 유지한다).
  const submitComplete = async () => {
    if (!completing || completeSubmitting) return;
    setCompleteSubmitting(true);
    const error = await applyContractComplete(
      completing,
      true,
      completeFile,
      completeFileName
    );
    setCompleteSubmitting(false);
    if (error) {
      setCompleteError(error);
      return;
    }
    setCompleting(null);
  };

  // 체험 계정 설정 팝업 상태 — 회원번호 조회(미리보기) 후 설정을 실행한다.
  const [trialTarget, setTrialTarget] =
    useState<ReferralAdminAmbassadorRow | null>(null);
  const [trialUserIdInput, setTrialUserIdInput] = useState("");
  // 연장 일수 — 조회 결과가 연장(extend)일 때만 사용, 기본 100일
  const [trialDaysInput, setTrialDaysInput] = useState("100");
  const [trialPreview, setTrialPreview] =
    useState<ReferralAdminTrialUserPreview | null>(null);
  const [trialLooking, setTrialLooking] = useState(false);
  const [trialSubmitting, setTrialSubmitting] = useState(false);
  const [trialCanceling, setTrialCanceling] = useState(false);
  const [trialError, setTrialError] = useState<string | null>(null);

  const openTrialPopup = (row: ReferralAdminAmbassadorRow) => {
    setTrialTarget(row);
    // 미설정 상태에서 앰버서더 신청이 들어와 있으면 신청 회원번호를 미리 채운다 — 조회 한 번으로 확인 가능
    setTrialUserIdInput(
      row.trialUserId
        ? String(row.trialUserId)
        : row.trialRequestUserId
          ? String(row.trialRequestUserId)
          : ""
    );
    setTrialDaysInput("100");
    setTrialPreview(null);
    setTrialError(null);
  };

  const lookupTrialUser = async () => {
    if (trialLooking) return;
    const userId = Number(trialUserIdInput.trim());
    if (!Number.isInteger(userId) || userId <= 0) {
      setTrialError("회원번호를 숫자로 입력해 주세요.");
      return;
    }
    setTrialLooking(true);
    setTrialError(null);
    setTrialPreview(null);
    try {
      const res = await adminApi.trialPreview(userId);
      if (res.code === 0 && res.result) setTrialPreview(res.result);
      else setTrialError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setTrialError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setTrialLooking(false);
    }
  };

  // 신청 내용과 조회 결과 불일치 — 차단하지 않고 표시만 한다 (설정/승인은 그대로 가능).
  // 조회 닉네임의 중복 순번 "(n)" 접미는 무시하고 비교한다.
  const trialRequestMismatch =
    !!trialTarget &&
    !trialTarget.trialUserId &&
    trialTarget.trialRequestUserId != null &&
    !!trialPreview &&
    (trialPreview.userId !== trialTarget.trialRequestUserId ||
      (!!trialTarget.trialRequestNickname &&
        trialPreview.nickname !== trialTarget.trialRequestNickname &&
        trialPreview.nickname.replace(/ \(\d+\)$/, "") !==
          trialTarget.trialRequestNickname));

  // 체험 취소 — 구독을 즉시 종료하고 앰버서더 연결을 해제한다 (해제 후 재신청/재설정 가능)
  const cancelTrial = async () => {
    if (!trialTarget?.trialUserId || trialCanceling) return;
    if (
      !window.confirm(
        `회원번호 ${trialTarget.trialUserId} 체험 계정의 구독을 종료할까요?`
      )
    ) {
      return;
    }
    setTrialCanceling(true);
    setTrialError(null);
    try {
      const res = await adminApi.trialCancel(trialTarget.ambassadorId);
      if (res.code === 0) {
        const targetAmbassadorId = trialTarget.ambassadorId;
        // 연동(trialUserId)은 유지하고 구독 상태만 종료로 — 목록에 "N · 종료"로 표기된다
        setRows((prev) =>
          prev.map((r) =>
            r.ambassadorId === targetAmbassadorId
              ? { ...r, trialUserSubscriptionStatus: "subscribing_expired" }
              : r
          )
        );
        setTrialTarget(null);
      } else {
        setTrialError(res.message || FALLBACK_ERROR_MESSAGE);
      }
    } catch (e) {
      setTrialError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setTrialCanceling(false);
    }
  };

  // 연장 일수 파싱 — extend 모드에서만 의미 있고, 양의 정수만 유효
  const trialDays = /^\d+$/.test(trialDaysInput.trim())
    ? Number(trialDaysInput.trim())
    : null;
  const trialDaysValid = trialDays !== null && trialDays > 0;

  const submitTrialSetup = async () => {
    if (!trialTarget || !trialPreview || trialSubmitting) return;
    const isExtend = trialPreview.mode === "extend";
    if (isExtend && !trialDaysValid) return;
    if (
      !window.confirm(
        `${trialPreview.nickname} 회원에게 "${trialModeLabel(
          trialPreview.mode,
          trialDays ?? undefined
        )}" 처리를 할까요?`
      )
    ) {
      return;
    }
    setTrialSubmitting(true);
    setTrialError(null);
    try {
      const res = await adminApi.trialSetup(
        trialTarget.ambassadorId,
        trialPreview.userId,
        isExtend && trialDays !== null ? trialDays : undefined
      );
      if (res.code === 0 && res.result) {
        const targetAmbassadorId = trialTarget.ambassadorId;
        const trialUserId = trialPreview.userId;
        setRows((prev) =>
          prev.map((r) =>
            r.ambassadorId === targetAmbassadorId
              ? // 설정 시 서버가 신청을 확인 완료로 마감하므로 대기 표시도 함께 지운다.
                // 설정 성공은 곧 구독 활성이므로 구독 상태도 함께 갱신한다 — 빠뜨리면 신규 설정 행이 "종료"로 표기된다
                {
                  ...r,
                  trialUserId,
                  trialUserSubscriptionStatus: "subscribing",
                  trialRequestUserId: null,
                  trialRequestNickname: null,
                }
              : r
          )
        );
        setTrialTarget(null);
      } else {
        setTrialError(res.message || FALLBACK_ERROR_MESSAGE);
      }
    } catch (e) {
      setTrialError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setTrialSubmitting(false);
    }
  };

  return (
    <section className="flex flex-col gap-4">
      {/* 검색·차수 필터 — 입력이 바뀌면 첫 페이지부터 다시 본다 */}
      <SectionHeader title="앰버서더 계약" count={`${visibleRows.length}명`}>
        <Input
          value={keyword}
          placeholder="활동명, 실명·법인명, 전화번호, 링크 검색"
          fullWidth={false}
          className="w-64"
          onChange={(e) => {
            setKeyword(e.target.value);
            setPage(1);
          }}
        />
        <Select
          value={groupFilter}
          aria-label="모집 차수 필터"
          fullWidth={false}
          className="w-32"
          onChange={(e) => {
            setGroupFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value={GROUP_FILTER_ALL}>전체 차수</option>
          <option value={GROUP_FILTER_NONE}>미지정</option>
          {groupOptions.map((groupNo) => (
            <option key={groupNo} value={String(groupNo)}>
              {groupNoLabel(groupNo)}
            </option>
          ))}
        </Select>
        {/* 처리 대기만 보기 — 색만으로는 다음 페이지의 대기 건을 놓친다 */}
        <Button
          variant={actionOnly ? "primary" : "secondary"}
          disabled={actionCount === 0 && !actionOnly}
          onClick={() => {
            setActionOnly((v) => !v);
            setPage(1);
          }}
        >
          처리 필요 {actionCount}
        </Button>
      </SectionHeader>

      {message && <ErrorBanner>{message}</ErrorBanner>}

      <Table>
        {/* 열 묶음 — 앰버서더가 계약 정보와 정산 정보를 따로 내듯 열도 같은 단위로 묶는다.
            묶음 경계는 TGroup·SortTH·TD 가 함께 왼쪽 선을 그어 세로로 이어진다 */}
        <THead
          groups={
            <>
              <TGroup span={5} />
              <TGroup span={2}>계약</TGroup>
              <TGroup span={1}>정산</TGroup>
              <TGroup span={3} />
            </>
          }
        >
          <SortTH
            label="차수"
            sortKey="groupNo"
            sort={sort}
            onSort={toggleSort}
          />
          <SortTH
            label="활동명"
            sortKey="name"
            sort={sort}
            onSort={toggleSort}
          />
          <SortTH
            label="링크"
            sortKey="codes"
            sort={sort}
            onSort={toggleSort}
          />
          <SortTH
            label="실명·법인명"
            sortKey="legalName"
            sort={sort}
            onSort={toggleSort}
          />
          <SortTH
            label="전화번호"
            sortKey="phoneNumber"
            sort={sort}
            onSort={toggleSort}
          />
          <SortTH
            label="형태"
            sortKey="contractType"
            sort={sort}
            onSort={toggleSort}
            className={GROUP_EDGE}
          />
          {/* 한 열이 상태와 체결 정보를 함께 보여주므로 정렬도 둘 다 남긴다 */}
          <TH>
            <span className="inline-flex items-center gap-1.5">
              <SortButton
                label="상태"
                sortKey="contractStatus"
                sort={sort}
                onSort={toggleSort}
              />
              <span className="text-slate-300">·</span>
              <SortButton
                label="체결일"
                sortKey="signing"
                sort={sort}
                onSort={toggleSort}
              />
            </span>
          </TH>
          <SortTH
            label="서류"
            sortKey="documents"
            sort={sort}
            onSort={toggleSort}
            className={GROUP_EDGE}
          />
          <SortTH
            label="체험 계정"
            sortKey="trial"
            sort={sort}
            onSort={toggleSort}
            className={GROUP_EDGE}
          />
          <SortTH
            label="가입일"
            sortKey="signedUpDate"
            sort={sort}
            onSort={toggleSort}
          />
          <TH />
        </THead>
        <tbody>
          {listLoading && <TSkeleton colSpan={11} />}
          {!listLoading && pagedRows.length === 0 && (
            <TEmpty colSpan={11}>
              {rows.length === 0
                ? "가입한 앰버서더가 없어요"
                : actionOnly
                  ? "처리할 건이 없어요"
                  : "조건에 맞는 앰버서더가 없어요"}
            </TEmpty>
          )}
          {pagedRows.map((row) => (
            <TRow
              key={row.ambassadorId}
              className={needsAdminAction(row) ? "bg-amber-50/60" : ""}
            >
              <TD>
                <GroupCell
                  row={row}
                  onSaved={(groupNo) => applyGroupNo(row.ambassadorId, groupNo)}
                />
              </TD>
              <TD className="font-medium">{row.name ?? "-"}</TD>
              <TD muted>{row.codes.length > 0 ? row.codes.join(", ") : "-"}</TD>
              {/* 계약서에 기재된 명의 — 활동명과 다를 수 있어 함께 본다 */}
              <TD>{contractLegalName(row)}</TD>
              <TD>
                {row.phoneNumber
                  ? formatPhoneNumberInput(row.phoneNumber)
                  : "-"}
                {!row.verified && (
                  <span className="ml-1 text-xs text-slate-400">(미인증)</span>
                )}
              </TD>

              {/* ── 계약 ── */}
              <TD muted className={GROUP_EDGE}>
                {/* 계약형태는 운영이 지정한다 — 미지정(계약 정보 미제출) 상태에서도 눌러서 채울 수 있다 */}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openContractInfo(row)}
                >
                  {row.contractType
                    ? contractTypeLabel(row.contractType)
                    : "지정"}
                </Button>
              </TD>
              <TD>
                {/* 완료 여부(배지)와 체결 방식·일시(보조 텍스트)를 한 칸에 둔다 —
                    "언제·어떻게 체결했나"와 "승인했나"는 같은 계약 건의 두 면이라
                    나란히 봐야 다음 할 일이 판단된다.
                    색은 처리가 필요한 자리(승인 대기)에만 준다 */}
                {row.contractSubmitted ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-md transition-opacity hover:opacity-70"
                    onClick={() => toggleContractComplete(row)}
                  >
                    <Badge tone={row.contractCompleted ? "idle" : "warn"}>
                      {contractStatusLabel(row)}
                    </Badge>
                    <span className="text-xs text-slate-400">
                      {signingLabel(row)}
                    </span>
                  </button>
                ) : (
                  // 계약 행 자체가 없으면 승인할 대상도, 체결 정보도 없다
                  <Badge tone="idle">{contractStatusLabel(row)}</Badge>
                )}
              </TD>

              {/* ── 정산 ── */}
              <TD className={GROUP_EDGE}>
                {/* 정산 서류(통장사본) 제출 여부 — 체결만 하고 서류를 안 낸 상태와 구분한다.
                    앰버서더가 안 낸 건은 운영이 받아둔 서류를 눌러서 대신 올린다 */}
                <button
                  type="button"
                  className="rounded-md transition-opacity hover:opacity-70"
                  onClick={() => openContractInfo(row)}
                >
                  <Badge tone="idle">{documentsLabel(row)}</Badge>
                </button>
              </TD>

              <TD className={GROUP_EDGE}>
                {/* 체험 계정 — 설정 여부만 상태로 보여주고, 눌러서 설정/변경 팝업을 연다.
                    회원번호는 목록에 노출하지 않는다 (팝업에서 조회·확인).
                    앰버서더 신청이 들어와 있으면 "승인 대기" 상태로 라벨링한다 */}
                {/* 상태가 있으면 배지(누르면 변경), 아직 없으면 액션 버튼 — 상태와 할 일을 섞지 않는다 */}
                {row.trialUserId || row.trialRequestUserId ? (
                  <button
                    type="button"
                    className="rounded-md transition-opacity hover:opacity-70"
                    onClick={() => openTrialPopup(row)}
                  >
                    {/* 라벨은 trialLabel 이 단일 소스 — 취소·만료된 체험은 연동을 유지한 채 "종료"다.
                        색만 여기서 정한다 (승인 대기 = 운영이 움직여야 하는 상태) */}
                    <Badge tone={needsTrialCheck(row) ? "warn" : "idle"}>
                      {trialLabel(row)}
                    </Badge>
                  </button>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openTrialPopup(row)}
                  >
                    설정
                  </Button>
                )}
              </TD>
              <TD muted>{row.signedUpDate ?? "-"}</TD>
              <TD className="text-right">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={detailLoading}
                  onClick={() => openDetail(row.ambassadorId)}
                >
                  상세
                </Button>
              </TD>
            </TRow>
          ))}
        </tbody>
      </Table>

      {/* 페이지 이동 — 한 페이지를 넘길 만큼 있을 때만 노출한다 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setPage(currentPage - 1)}
          >
            이전
          </Button>
          <span className="text-sm tabular-nums text-slate-500">
            {currentPage} / {totalPages}
          </span>
          <Button
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => setPage(currentPage + 1)}
          >
            다음
          </Button>
        </div>
      )}

      {detail && (
        <ContractDetailPanel detail={detail} onClose={() => setDetail(null)} />
      )}

      {/* 계약 승인 팝업 — 승인 전에는 사본을 함께 보관하고(선택), 승인 후에는 사본 교체·승인 취소를 한다 */}
      {/* 계약 정보 수기 입력 팝업 — 계약형태·당사자 정보·정산 서류를 운영이 대신 채운다 */}
      {infoTarget && (
        <Modal
          title="계약 정보 수정"
          subject={ambassadorSubject(infoTarget)}
          description="앰버서더가 직접 내지 않은 계약 정보를 운영이 대신 채워요. 비워 둔 칸은 기존 값을 그대로 둬요. 정산 정보(주민등록번호·계좌)는 앰버서더가 직접 제출해야 해요."
          width="560px"
          onClose={() => setInfoTarget(null)}
          footer={
            <Button
              variant="primary"
              loading={infoSubmitting}
              loadingLabel="저장 중"
              disabled={infoLoading || !infoDirty}
              onClick={submitContractInfo}
            >
              저장
            </Button>
          }
        >
          <div className="flex flex-col gap-4">
            <Field
              label="계약형태"
              helper="사업자로 바꾸면 사업자등록증이 함께 필요해요."
            >
              <Select
                value={infoType}
                disabled={infoLoading}
                onChange={(e) =>
                  setInfoType(e.target.value as AmbassadorContractType)
                }
              >
                <option value="freelancer">프리랜서</option>
                <option value="business_general">사업자(일반)</option>
                <option value="business_simplified">사업자(간이)</option>
              </Select>
            </Field>

            {/* 계약서에 기재할 이름 — 형태에 따라 실명/사업자명 하나만 받는다 */}
            {infoIsBusiness ? (
              <Field label="사업자명">
                <Input
                  value={infoBusinessName}
                  disabled={infoLoading}
                  placeholder="주식회사 오웰헬스"
                  onChange={(e) => setInfoBusinessName(e.target.value)}
                />
              </Field>
            ) : (
              <Field label="실명">
                <Input
                  value={infoRealName}
                  disabled={infoLoading}
                  placeholder="홍길동"
                  onChange={(e) => setInfoRealName(e.target.value)}
                />
              </Field>
            )}

            <Field label="계약서 수신 이메일">
              <Input
                value={infoEmail}
                disabled={infoLoading}
                placeholder="name@example.com"
                onChange={(e) => setInfoEmail(e.target.value)}
              />
            </Field>

            <Field
              label="계약 연락처"
              helper="비우면 계정 로그인 번호를 써요. 사업자는 유선 번호도 넣을 수 있어요."
            >
              <Input
                value={infoPhoneNumber}
                disabled={infoLoading}
                placeholder="010-1234-5678"
                onChange={(e) => setInfoPhoneNumber(e.target.value)}
              />
            </Field>

            <FileDropField
              label="통장사본"
              existing={infoDetail?.bankbookFile}
              existingFileName={infoDetail?.bankbookFileName}
              fileBaseName={`통장사본-${fileOwnerName(infoTarget)}`}
              fileName={infoBankbookName}
              onPick={(file) =>
                handleInfoFile(file, setInfoBankbook, setInfoBankbookName)
              }
            />

            {infoIsBusiness && (
              <FileDropField
                label="사업자등록증"
                existing={infoDetail?.businessRegistrationFile}
                existingFileName={infoDetail?.businessRegistrationFileName}
                fileBaseName={`사업자등록증-${fileOwnerName(infoTarget)}`}
                fileName={infoBusinessRegName}
                onPick={(file) =>
                  handleInfoFile(
                    file,
                    setInfoBusinessReg,
                    setInfoBusinessRegName
                  )
                }
              />
            )}

            {infoError && <ErrorBanner>{infoError}</ErrorBanner>}
          </div>
        </Modal>
      )}

      {completing && (
        <Modal
          title={
            completing.contractCompleted ? "계약서 사본 교체" : "계약 승인"
          }
          subject={ambassadorSubject(completing)}
          description={
            completing.contractCompleted
              ? "보관된 계약서 사본을 올리거나 교체할 수 있어요. 승인 상태와 승인 시각은 그대로 유지되고, 앰버서더는 계약 페이지에서 사본을 확인할 수 있어요 (이미지·PDF, 5MB 이하)."
              : "승인하면 앰버서더가 링크를 만들 수 있게 되고, 앰버서더도 계약 페이지에서 사본을 확인할 수 있어요. 체결된 계약서 사본을 함께 보관하는 걸 권장해요 (이미지·PDF, 5MB 이하)."
          }
          onClose={() => setCompleting(null)}
          footer={
            <>
              {/* 확인 계열(승인·사본 저장)은 오른쪽(기본) — 파괴적인 승인 취소만 왼쪽으로 뗀다 */}
              {completing.contractCompleted && (
                <Button
                  variant="danger"
                  className="mr-auto"
                  disabled={completeSubmitting}
                  onClick={releaseComplete}
                >
                  승인 취소
                </Button>
              )}
              <Button
                variant="primary"
                // 완료 상태에서는 사본 교체만 하므로 파일을 고르지 않으면 저장할 게 없다
                disabled={completing.contractCompleted && !completeFile}
                loading={completeSubmitting}
                loadingLabel="처리 중…"
                onClick={submitComplete}
              >
                {completing.contractCompleted ? "사본 저장" : "승인"}
              </Button>
            </>
          }
        >
          {/* 전자서명 체결 건은 사본이 비어 있는 게 정상 — 서명된 계약서는 체결본 PDF 다.
              사본 칸만 보면 "계약서가 없다"로 읽히므로 여기서 바로 받을 수 있게 한다 */}
          {completing.signedAt && (
            <div className="mt-4 rounded-md border border-[#0F55A7]/20 bg-[#DCEFFA] p-3">
              <p className="text-sm font-semibold text-[#0F55A7]">
                전자서명으로 체결된 계약이에요
              </p>
              <p className="mt-1 text-sm leading-relaxed text-[#0F55A7]/90">
                서명된 계약서는 아래 체결본 PDF 예요. 사본 업로드는 외부
                전자서명으로 체결한 건을 따로 보관할 때만 쓰면 돼요.
              </p>
              <div className="mt-3">
                <SignedContractPdfButton
                  ambassadorId={completing.ambassadorId}
                  fileBaseName={`전자계약서-${fileOwnerName(completing)}`}
                />
              </div>
            </div>
          )}
          {/* 보관된 사본 — 이미지는 섬네일, PDF 는 다운로드로 내용을 확인한 뒤 교체하게 한다 */}
          <div className="mt-4">
            {completeExistingLoading ? (
              <div className="flex gap-3 text-sm">
                <span className="w-32 shrink-0 text-slate-500">
                  보관된 사본
                </span>
                <span className="h-5 w-24 animate-pulse rounded bg-slate-100" />
              </div>
            ) : (
              <ContractFileView
                label="보관된 사본"
                dataUrl={completeExisting}
                fileName={completeExistingName}
                fileBaseName={`계약서-${fileOwnerName(completing)}`}
              />
            )}
          </div>
          {/* 드롭존 — 클릭 선택과 드래그앤드롭 모두 지원 */}
          <div
            className={`mt-4 flex items-center gap-3 rounded-md border border-dashed p-3 text-sm transition-colors ${
              completeDragging
                ? "border-[#0F55A7] bg-[#DCEFFA]"
                : "border-slate-300 bg-slate-50"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setCompleteDragging(true);
            }}
            onDragLeave={(e) => {
              // 자식 요소로 이동할 때 발생하는 leave 는 무시한다 (깜빡임 방지)
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setCompleteDragging(false);
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              setCompleteDragging(false);
              handleCompleteFile(e.dataTransfer.files?.[0]);
            }}
          >
            <label className="inline-flex h-8 shrink-0 cursor-pointer items-center rounded-md border border-slate-300 bg-white px-2.5 font-medium text-slate-700 transition-colors hover:bg-slate-50">
              파일 선택
              <input
                type="file"
                className="hidden"
                accept="image/png,image/jpeg,image/gif,image/webp,image/heic,image/heif,application/pdf"
                onChange={(e) => {
                  handleCompleteFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </label>
            <span
              className={
                completeFileName ? "break-all text-slate-900" : "text-slate-400"
              }
            >
              {completeFileName ?? "파일을 끌어다 놓거나 선택하세요"}
            </span>
          </div>
          {completeError && (
            <div className="mt-3">
              <ErrorBanner>{completeError}</ErrorBanner>
            </div>
          )}
        </Modal>
      )}

      {/* 체험 계정 설정 팝업 — 회원번호 조회(미리보기) 후 설정 실행 */}
      {trialTarget && (
        <Modal
          title="체험 계정 설정"
          subject={ambassadorSubject(trialTarget)}
          description="회원번호를 조회해 확인한 뒤 체험을 설정해요. 신규 회원은 인플루언서 체험(100일)으로 시작되고, 이미 결제해 이용 중이거나 재구독한 회원은 재결제하듯 연장·재개돼요."
          onClose={() => setTrialTarget(null)}
          footer={
            <>
              {/* 확인 계열(체험 설정하기)은 오른쪽(기본), 파괴 계열(체험 종료)은 왼쪽으로 뗀다 */}
              {/* 이용 중이면 "체험 종료", 이미 종료됐으면 상태만 표시 (연결 끊기는 미제공) */}
              {!!trialTarget.trialUserId &&
                (trialTarget.trialUserSubscriptionStatus === "subscribing" ? (
                  <Button
                    variant="danger"
                    className="mr-auto"
                    disabled={trialSubmitting}
                    loading={trialCanceling}
                    loadingLabel="종료 중…"
                    onClick={cancelTrial}
                  >
                    체험 종료
                  </Button>
                ) : (
                  <span className="mr-auto text-sm text-slate-400">
                    체험 종료됨
                  </span>
                ))}
              <Button
                variant="primary"
                disabled={
                  !trialPreview ||
                  (trialPreview.mode === "extend" && !trialDaysValid)
                }
                loading={trialSubmitting}
                loadingLabel="설정 중…"
                onClick={submitTrialSetup}
              >
                체험 설정하기
              </Button>
            </>
          }
        >
          {/* 앰버서더가 신청한 건이면 신청 내용을 보여준다 — 조회 결과 닉네임과 대조용 */}
          {!trialTarget.trialUserId && trialTarget.trialRequestUserId && (
            <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              신청 회원번호 {trialTarget.trialRequestUserId}
              {trialTarget.trialRequestNickname
                ? ` · 닉네임 ${trialTarget.trialRequestNickname}`
                : ""}
            </p>
          )}

          <div className="mt-4 flex gap-2">
            <Input
              inputMode="numeric"
              placeholder="회원번호 (userId)"
              value={trialUserIdInput}
              onChange={(e) => {
                setTrialUserIdInput(e.target.value);
                // 입력이 바뀌면 이전 조회 결과로 설정하지 못하게 미리보기를 비운다
                setTrialPreview(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") lookupTrialUser();
              }}
            />
            <Button
              loading={trialLooking}
              loadingLabel="조회 중…"
              onClick={lookupTrialUser}
            >
              조회
            </Button>
          </div>

          {trialPreview && (
            <div className="mt-4 flex flex-col gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
              <InfoRow label="닉네임">{trialPreview.nickname}</InfoRow>
              <InfoRow label="이메일">{trialPreview.email ?? "-"}</InfoRow>
              <InfoRow label="가입 방식">
                {oauthIssuerLabel(trialPreview.oauthIssuer)}
              </InfoRow>
              <InfoRow label="가입일">
                {trialPreview.signedUpDate ?? "-"}
              </InfoRow>
              <InfoRow label="프로그램 이용 상태">
                {progressStatusLabel(trialPreview.progressStatus)}
              </InfoRow>
              {/* 구독 만료일 — 종료된 계정은 언제 끝났는지, 이용 중이면 언제 끝나는지가
                  적용 방식(재개/연장) 판단 근거가 된다. 구독 이력이 없으면 숨긴다 */}
              {trialPreview.subscriptionExpirationDate && (
                <InfoRow
                  label={
                    trialPreview.subscriptionStatus === "subscribing"
                      ? "만료 예정일"
                      : "만료일"
                  }
                >
                  {trialPreview.subscriptionExpirationDate}
                </InfoRow>
              )}
              <InfoRow label="적용 방식">
                <span className="font-medium">
                  {trialModeLabel(trialPreview.mode, trialDays ?? undefined)}
                </span>
              </InfoRow>
              {/* 연장일 때만 일수를 조정할 수 있다 — 기본 100일 */}
              {trialPreview.mode === "extend" && (
                <InfoRow label="연장 일수">
                  <span className="flex items-center gap-1.5">
                    <Input
                      inputMode="numeric"
                      invalid={!trialDaysValid}
                      sizing="sm"
                      fullWidth={false}
                      className="w-20"
                      value={trialDaysInput}
                      onChange={(e) => setTrialDaysInput(e.target.value)}
                    />
                    일
                    {!trialDaysValid && (
                      <span className="ml-1 text-xs text-rose-600">
                        숫자만 입력해 주세요
                      </span>
                    )}
                  </span>
                </InfoRow>
              )}
            </div>
          )}

          {/* 불일치는 표시만 — 설정 버튼은 막지 않는다 */}
          {trialRequestMismatch && trialTarget && trialPreview && (
            <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {`신청 내용과 달라요. 신청 ${trialTarget.trialRequestUserId}${
                trialTarget.trialRequestNickname
                  ? ` · ${trialTarget.trialRequestNickname}`
                  : ""
              } / 조회 ${trialPreview.userId} · ${trialPreview.nickname}`}
            </p>
          )}

          {trialError && (
            <div className="mt-3">
              <ErrorBanner>{trialError}</ErrorBanner>
            </div>
          )}
        </Modal>
      )}
    </section>
  );
}

// 체결본 PDF 내려받기 — 서버가 스냅샷을 계약서 양식(A4)으로 렌더링한다.
// 정상 응답은 PDF, 비즈니스 오류는 JSON Blob 으로 오므로 content-type 으로 갈라 본다.
// 성공이면 null, 실패면 표시할 메시지를 반환한다 (호출처가 상태·표시 위치를 정한다).
const downloadContractSignPdf = async (
  ambassadorId: number,
  fileBaseName: string
): Promise<string | null> => {
  try {
    const res = await adminApi.ambassadorContractSignPdf(ambassadorId);
    const blob = res.data;
    if (blob.type !== "application/pdf") {
      const parsed = JSON.parse(await blob.text());
      return parsed?.message || FALLBACK_ERROR_MESSAGE;
    }
    // 파일명은 서버가 정한다 — 헤더를 못 읽으면(CORS 미노출·구버전) 규칙 이름으로 떨어진다
    saveBlobAsFile(
      blob,
      fileNameFromContentDisposition(res.headers?.["content-disposition"]) ||
        `${fileBaseName}.pdf`
    );
    return null;
  } catch (e) {
    return FALLBACK_ERROR_MESSAGE;
  }
};

// 체결본 PDF 다운로드 버튼 — 자체 로딩·오류 상태를 갖는다 (계약 승인 팝업처럼 상태를 따로 둘 곳이 없는 자리용).
function SignedContractPdfButton({
  ambassadorId,
  fileBaseName,
}: {
  ambassadorId: number;
  fileBaseName: string;
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <>
      <Button
        size="sm"
        loading={downloading}
        loadingLabel="PDF 생성 중…"
        onClick={async () => {
          setDownloading(true);
          setError(await downloadContractSignPdf(ambassadorId, fileBaseName));
          setDownloading(false);
        }}
      >
        체결본 PDF 다운로드
      </Button>
      {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
    </>
  );
}

function ContractDetailPanel({
  detail,
  onClose,
}: {
  detail: ReferralAdminAmbassadorContractDetail;
  onClose: () => void;
}) {
  const fileBaseName = fileOwnerName(detail);
  // 전자서명 체결본 전문 접기/펼치기
  const [signContentOpen, setSignContentOpen] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const downloadSignPdf = async () => {
    if (pdfDownloading) return;
    setPdfDownloading(true);
    setPdfError(
      await downloadContractSignPdf(
        detail.ambassadorId,
        `전자계약서-${fileBaseName}`
      )
    );
    setPdfDownloading(false);
  };

  return (
    // 항목이 많아(정산 정보·전자서명 기록·체결본 전문) 팝업을 넓게 쓴다
    <Modal
      title="계약 정보 상세"
      subject={detail.name ?? undefined}
      width="720px"
      onClose={onClose}
    >
      {detail.contractType ? (
        <div className="flex flex-col gap-2.5">
          <InfoRow label="계약형태">
            {contractTypeLabel(detail.contractType)}
          </InfoRow>
          {/* 체결 방식 — 앰버서더 페이지 체결 건은 서명된 계약서가 체결본 PDF 이고,
              외부 체결 건은 업로드된 사본이 원본이다 */}
          <InfoRow label="체결 방식">
            {detail.sign ? (
              <Badge tone="idle">앰버서더 페이지</Badge>
            ) : (
              <span className="text-slate-500">
                외부 (전자서명 서비스로 이메일을 주고받아 체결, 사본 업로드로
                보관)
              </span>
            )}
          </InfoRow>
          {detail.realName && <InfoRow label="실명">{detail.realName}</InfoRow>}
          {detail.businessName && (
            <InfoRow label="사업자명">{detail.businessName}</InfoRow>
          )}
          <InfoRow label="계약서 수신 이메일">
            {detail.contractEmail ?? "-"}
          </InfoRow>
          <InfoRow label="핸드폰 번호">
            {detail.contractPhoneNumber
              ? formatPhoneNumberInput(detail.contractPhoneNumber)
              : "-"}
          </InfoRow>
          <InfoRow label="활동명">{detail.name ?? "-"}</InfoRow>
          {/* 수수료는 링크(코드)에 붙어 있어 코드와 함께 보여준다 — 계약 조건 확인용, 수정은 링크 관리 탭에서 */}
          <InfoRow label="수수료">
            {detail.codeFees?.length ? (
              <span className="flex flex-col gap-0.5">
                {detail.codeFees.map((fee) => (
                  <span key={fee.code}>
                    <span className="tabular-nums">
                      {wonLabel(fee.perPayerAmount)}
                    </span>
                    <span className="ml-1.5 text-xs text-slate-400">
                      {fee.code}
                    </span>
                  </span>
                ))}
              </span>
            ) : (
              "연결된 링크 없음"
            )}
          </InfoRow>
          <ContractFileView
            label="통장사본"
            dataUrl={detail.bankbookFile}
            fileName={detail.bankbookFileName}
            fileBaseName={`통장사본-${fileBaseName}`}
          />
          {/* 사업자등록증은 사업자 유형의 필수 항목 — 프리랜서 상세에는 표시하지 않는다 */}
          {detail.contractType !== "freelancer" && (
            <ContractFileView
              label="사업자등록증"
              dataUrl={detail.businessRegistrationFile}
              fileName={detail.businessRegistrationFileName}
              fileBaseName={`사업자등록증-${fileBaseName}`}
            />
          )}
          <ContractFileView
            label="계약서 사본"
            dataUrl={detail.contractFile}
            fileName={detail.contractFileName}
            fileBaseName={`계약서-${fileBaseName}`}
          />

          {/* 정산 정보 — 전자계약 플로우의 서류 제출 단계에서 수집. 주민번호는 원천징수 신고용 */}
          {(detail.bankName ||
            detail.bankAccount ||
            detail.residentRegistrationNumber) && (
            <>
              <p className="mt-3 border-t border-slate-200 pt-4 text-sm font-semibold text-slate-900">
                정산 정보
              </p>
              {detail.residentRegistrationNumber && (
                <InfoRow label="주민등록번호">
                  {formatRrn(detail.residentRegistrationNumber)}
                </InfoRow>
              )}
              <InfoRow label="정산 계좌">
                {`${detail.bankName ?? "-"} ${detail.bankAccount ?? ""}`.trim()}
              </InfoRow>
              <InfoRow label="예금주">
                {detail.accountHolderName ?? "-"}
                {detail.accountHolderMismatch && (
                  <span className="ml-2 font-medium text-amber-700">
                    계약서상 이름과 달라요. 승인 전 확인이 필요해요.
                  </span>
                )}
              </InfoRow>
              {/* 서류 제출 단계 동의 시각 — 세무 처리 확인 / 개인정보 수집·이용 (재제출 시 갱신) */}
              {(detail.taxAgreedAt || detail.privacyAgreedAt) && (
                <InfoRow label="제출 단계 동의">
                  {[
                    detail.taxAgreedAt &&
                      `세무 처리 ${formatSignedAtText(detail.taxAgreedAt)}`,
                    detail.privacyAgreedAt &&
                      `개인정보 ${formatSignedAtText(detail.privacyAgreedAt)}`,
                  ]
                    .filter(Boolean)
                    .join(" / ")}
                </InfoRow>
              )}
            </>
          )}

          {/* 전자서명 체결 기록 — 이메일로 체결한 건은 없음 */}
          {detail.sign && (
            <>
              <p className="mt-3 border-t border-slate-200 pt-4 text-sm font-semibold text-slate-900">
                전자서명
              </p>
              <InfoRow label="체결 시각">
                {formatSignedAtText(detail.sign.signedAt)}
              </InfoRow>
              <InfoRow label="템플릿 버전">
                {detail.sign.templateVersion}
              </InfoRow>
              <InfoRow label="스냅샷 해시">{detail.sign.snapshotHash}</InfoRow>
              <InfoRow label="체결 IP">{detail.sign.ip ?? "-"}</InfoRow>
              <InfoRow label="User-Agent">
                {detail.sign.userAgent ?? "-"}
              </InfoRow>
              <InfoRow label="동의 항목">
                {[
                  detail.sign.agreedKeyTerms && "핵심 내용 확인",
                  detail.sign.agreedFull && "전문 확인·체결 동의",
                ]
                  .filter(Boolean)
                  .join(" / ") || "-"}
              </InfoRow>
              <InfoRow label="주소">{detail.sign.address}</InfoRow>
              {detail.sign.representativeName && (
                <InfoRow label="대표자">
                  {detail.sign.representativeName}
                </InfoRow>
              )}
              {detail.sign.businessRegistrationNumber && (
                <InfoRow label="사업자등록번호">
                  {detail.sign.businessRegistrationNumber}
                </InfoRow>
              )}
              <div className="mt-1 flex gap-2">
                <Button size="sm" onClick={() => setSignContentOpen((v) => !v)}>
                  {signContentOpen ? "체결본 접기" : "체결본 전문 보기"}
                </Button>
                <Button
                  size="sm"
                  loading={pdfDownloading}
                  loadingLabel="PDF 생성 중…"
                  onClick={downloadSignPdf}
                >
                  PDF 다운로드
                </Button>
              </div>
              {pdfError && <p className="text-xs text-rose-600">{pdfError}</p>}
              {signContentOpen && (
                <div className="max-h-96 overflow-y-auto rounded-lg border border-slate-200 bg-white p-4">
                  <ContractDocument source={detail.sign.content} />
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-500">
          아직 계약 정보를 제출하지 않았어요.
        </p>
      )}
    </Modal>
  );
}

// 서류 업로드 칸 — 기존 제출본을 먼저 보여주고 그 아래에서 새 파일을 고른다.
// 클릭 선택과 드래그앤드롭을 모두 받는다 (드롭된 파일은 accept 필터를 안 거쳐 호출처가 형식을 검사한다).
// fileName: 이번에 고른 파일 이름(드롭존 표시용). existingFileName: 이미 올라가 있는 파일의 원본 이름.
function FileDropField({
  label,
  existing,
  existingFileName,
  fileBaseName,
  fileName,
  onPick,
}: {
  label: string;
  existing?: string | null;
  existingFileName?: string | null;
  fileBaseName: string;
  fileName: string | null;
  onPick: (file?: File) => void;
}) {
  const [dragging, setDragging] = useState(false);
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <ContractFileView
        label="기존 제출본"
        dataUrl={existing}
        fileBaseName={fileBaseName}
        fileName={existingFileName}
      />
      <div
        className={`flex items-center gap-3 rounded-md border border-dashed p-3 text-sm transition-colors ${
          dragging
            ? "border-[#0F55A7] bg-[#DCEFFA]"
            : "border-slate-300 bg-slate-50"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDragging(false);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          onPick(e.dataTransfer.files?.[0]);
        }}
      >
        <label className="inline-flex h-8 shrink-0 cursor-pointer items-center rounded-md border border-slate-300 bg-white px-2.5 font-medium text-slate-700 transition-colors hover:bg-slate-50">
          파일 선택
          <input
            type="file"
            className="hidden"
            accept="image/png,image/jpeg,image/gif,image/webp,image/heic,image/heif,application/pdf"
            onChange={(e) => {
              onPick(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </label>
        <span
          className={fileName ? "break-all text-slate-900" : "text-slate-400"}
        >
          {fileName ?? "파일을 끌어다 놓거나 선택하세요"}
        </span>
      </div>
    </div>
  );
}

// fileName: 업로드 당시의 원본 파일명. 없으면(컬럼 추가 이전에 올라간 건) fileBaseName 으로 이름을 짓는다.
function ContractFileView({
  label,
  dataUrl,
  fileBaseName,
  fileName,
}: {
  label: string;
  dataUrl?: string | null;
  fileBaseName: string;
  fileName?: string | null;
}) {
  if (!dataUrl) {
    return (
      <div className="flex gap-3 text-sm">
        <span className="w-32 shrink-0 text-slate-500">{label}</span>
        <span className="text-slate-400">없음</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-3 text-sm">
        <span className="w-32 shrink-0 text-slate-500">{label}</span>
        <a
          className="font-medium text-[#0F55A7] underline underline-offset-2 hover:text-[#0C4585]"
          href={dataUrl}
          download={fileName || `${fileBaseName}.${fileExtOf(dataUrl)}`}
        >
          다운로드
        </a>
      </div>
      {dataUrl.startsWith("data:image") && (
        <img
          className="ml-[8.75rem] max-h-48 w-fit rounded-md border border-slate-200 object-contain"
          src={dataUrl}
          alt={label}
        />
      )}
    </div>
  );
}

// ---------- 앰버서더 성과 ----------

// 기간 프리셋 — 표와 정렬이 전부 이 하나로 움직인다. custom 은 from/to 를 직접 입력받는다.
type PerformancePreset =
  | "today"
  | "7d"
  | "30d"
  | "thisMonth"
  | "lastMonth"
  | "custom";

const PERFORMANCE_PRESETS: { key: PerformancePreset; label: string }[] = [
  { key: "today", label: "오늘" },
  { key: "7d", label: "7일" },
  { key: "30d", label: "30일" },
  { key: "thisMonth", label: "이번 달" },
  { key: "lastMonth", label: "지난 달" },
  { key: "custom", label: "직접" },
];

// 프리셋 → [시작일, 종료일] (양끝 포함). custom 은 기존 입력값을 유지하므로 여기서 다루지 않는다.
const presetRange = (preset: PerformancePreset): [string, string] => {
  const today = localToday();
  switch (preset) {
    case "today":
      return [today, today];
    case "7d":
      return [addDays(today, -6), today];
    case "30d":
      return [addDays(today, -29), today];
    case "thisMonth":
      return [`${today.slice(0, 7)}-01`, today];
    case "lastMonth": {
      const lastDayOfLastMonth = addDays(`${today.slice(0, 7)}-01`, -1);
      return [`${lastDayOfLastMonth.slice(0, 7)}-01`, lastDayOfLastMonth];
    }
    default:
      return [today, today];
  }
};

// 자동 갱신 주기 — 이 화면의 이벤트는 하루 수십 건 규모라 10분이면 "실시간"으로 충분하다.
// 더 자주 돌면 코드 전체의 user_referral 을 그만큼 다시 훑는다 (performanceOverview).
const PERFORMANCE_REFRESH_MS = 10 * 60_000;
const PERFORMANCE_REFRESH_LABEL = "10분마다 자동 갱신";

type PerformanceSortKey =
  | "name"
  | "signupCount"
  | "payerCount"
  | "refundCount"
  | "revenueAmount"
  | "settlementAmount";

type PerformanceSortState = { key: PerformanceSortKey; desc: boolean };

const performanceSortValue = (
  row: ReferralAdminPerformanceRow,
  key: PerformanceSortKey
): number | string => {
  const metrics = row.metrics;
  switch (key) {
    case "name":
      return row.name ?? row.code;
    case "settlementAmount":
      return metrics.settlementAmount ?? 0;
    default:
      return metrics[key];
  }
};

const wonLabel = (amount?: number | null) =>
  amount != null ? `${amount.toLocaleString()}원` : "-";

// 최근 14일 결제 추이 — 값 자체보다 "움직이는지"를 보는 자리라 축·눈금 없이 막대만 둔다.
// 최댓값을 기준으로 높이를 맞추고, 0인 날은 회색 바닥선으로 남겨 날짜 간격을 유지한다.
function Sparkline({ values }: { values: number[] }) {
  const max = Math.max(1, ...values);
  const total = values.reduce((sum, v) => sum + v, 0);
  return (
    <span
      className="inline-flex h-5 items-end gap-[2px] align-middle"
      title={`최근 14일 결제 ${total}건`}
    >
      {values.map((value, i) => (
        <span
          key={i}
          className={`w-[3px] rounded-sm ${
            value > 0 ? "bg-[#0F55A7]" : "bg-slate-200"
          }`}
          style={{
            height: value > 0 ? `${Math.max(20, (value / max) * 100)}%` : "10%",
          }}
        />
      ))}
      <span className="sr-only">최근 14일 결제 {total}건</span>
    </span>
  );
}

// 증감 표기 — 화살표와 숫자가 뜻을 전달하고 색은 거들기만 한다 (색각 이상 대응).
// 환불처럼 줄어드는 게 좋은 지표는 lowerIsBetter 로 색을 뒤집는다.
function LiveDelta({
  value,
  previous,
  currency = false,
  lowerIsBetter = false,
}: {
  value: number;
  previous: number;
  currency?: boolean;
  lowerIsBetter?: boolean;
}) {
  const diff = value - previous;
  if (diff === 0) {
    return <span className="text-xs text-slate-400">변화 없음</span>;
  }
  const good = lowerIsBetter ? diff < 0 : diff > 0;
  const amount = Math.abs(diff);
  return (
    <span
      className={`text-xs tabular-nums ${
        good ? "text-emerald-600" : "text-rose-600"
      }`}
    >
      {diff > 0 ? "▲" : "▼"}
      {currency ? amount.toLocaleString() : amount}
    </span>
  );
}

function LiveStat({
  label,
  value,
  previous,
  currency = false,
  lowerIsBetter = false,
}: {
  label: string;
  value: number;
  previous: number;
  currency?: boolean;
  lowerIsBetter?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-lg font-semibold tabular-nums leading-tight text-slate-900">
        {currency ? `${value.toLocaleString()}원` : value.toLocaleString()}
      </span>
      <LiveDelta
        value={value}
        previous={previous}
        currency={currency}
        lowerIsBetter={lowerIsBetter}
      />
    </div>
  );
}

function LivePanel({
  label,
  compareLabel,
  metrics,
  compare,
}: {
  label: string;
  compareLabel: string;
  metrics: ReferralAdminPerformanceMetrics;
  compare: ReferralAdminPerformanceMetrics;
}) {
  return (
    <div className="bg-white px-4 py-3.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <p className="text-sm font-semibold text-slate-900">{label}</p>
        <p className="text-xs text-slate-400">{compareLabel} 대비</p>
      </div>
      <div className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
        <LiveStat
          label="가입"
          value={metrics.signupCount}
          previous={compare.signupCount}
        />
        <LiveStat
          label="결제"
          value={metrics.payerCount}
          previous={compare.payerCount}
        />
        <LiveStat
          label="환불"
          value={metrics.refundCount}
          previous={compare.refundCount}
          lowerIsBetter
        />
        <LiveStat
          label="매출"
          value={metrics.revenueAmount}
          previous={compare.revenueAmount}
          currency
        />
      </div>
    </div>
  );
}

// 실시간 스트립 — 선택 기간과 무관하게 항상 "지금"을 본다. 범위는 아래 표와 같은 앰버서더 링크 전체다.
function LiveStrip({ live }: { live: ReferralAdminPerformanceLive }) {
  return (
    <div className="grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 lg:grid-cols-2">
      <LivePanel
        label="오늘"
        compareLabel="어제 하루"
        metrics={live.today}
        compare={live.yesterday}
      />
      <LivePanel
        label="이번 달"
        compareLabel="지난달 같은 일자까지"
        metrics={live.thisMonth}
        compare={live.lastMonthToDate}
      />
    </div>
  );
}

// 성과 표의 정렬 열 제목 — 앰버서더 목록의 SortTH 와 표식은 같지만 정렬 키 집합이 달라 따로 둔다.
// 여기는 해제 상태가 없다 (기본 정렬이 "결제 많은 순"이라 되돌릴 서버 순서가 따로 없다).
function PerformanceSortTH({
  label,
  sortKey,
  sort,
  onSort,
  numeric = false,
}: {
  label: string;
  sortKey: PerformanceSortKey;
  sort: PerformanceSortState;
  onSort: (key: PerformanceSortKey) => void;
  numeric?: boolean;
}) {
  const active = sort.key === sortKey;
  return (
    <TH
      numeric={numeric}
      aria-sort={active ? (sort.desc ? "descending" : "ascending") : "none"}
    >
      <button
        type="button"
        className="inline-flex items-center gap-1 transition-colors hover:text-slate-900"
        onClick={() => onSort(sortKey)}
      >
        {label}
        <span className={active ? "text-[#0F55A7]" : "text-slate-300"}>
          {active ? (sort.desc ? "↓" : "↑") : "↕"}
        </span>
      </button>
    </TH>
  );
}

const POST_COL_SPAN = 5;

// 채널 아이콘 — 앰버서더 화면과 같은 로고를 쓴다. 키는 서버가 주는 표기(platform)라,
// 그 표기가 바뀌면 엉뚱한 로고를 다는 대신 중립 링크 아이콘으로 떨어진다.
const POST_PLATFORM_ICON: Record<string, typeof LinkRoundedIcon> = {
  인스타그램: InstagramIcon,
  유튜브: YouTubeIcon,
};

type PostSortKey = "groupNo" | "name" | "progress" | "lastPostedDate";

const POST_SORT_VALUE: Record<
  PostSortKey,
  (row: ReferralAdminAmbassadorPostRow) => string | number | null
> = {
  groupNo: (r) => r.groupNo ?? null,
  name: (r) => r.name || null,
  // 미달성을 찾는 게 이 화면의 목적이라 등록 건수로 세운다 (오름차순이 곧 "덜 올린 사람 먼저").
  progress: (r) => r.postCount,
  lastPostedDate: (r) => r.lastPostedDate || null,
};

// 앰버서더별 게시 현황 — 계약 제3조의 필수 게시(3건) 이행 상태. 조회 전용이다 (승인·반려 없음).
// 한 행이 앰버서더 1명인 이유는 게시물만 나열하면 0건인 사람이 목록에서 사라지기 때문이다.
function AmbassadorPostSection() {
  const [rows, setRows] = useState<ReferralAdminAmbassadorPostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [keyword, setKeyword] = useState("");
  const [groupFilter, setGroupFilter] = useState<string>(GROUP_FILTER_ALL);
  // 아직 한 건도 안 올린 앰버서더만 — 이 화면에서 가장 자주 하는 질문이다
  const [emptyOnly, setEmptyOnly] = useState(false);
  const [page, setPage] = useState(1);
  // 최근 등록이 위로 — 어떤 게시물이 새로 올라왔는지가 첫 화면에서 보여야 한다
  const [sort, setSort] = useState<{
    key: PostSortKey;
    dir: "asc" | "desc";
  } | null>({ key: "lastPostedDate", dir: "desc" });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await adminApi.ambassadorPostList();
        if (!alive) return;
        if (res.code === 0) setRows(res.result ?? []);
        else setMessage(res.message || FALLBACK_ERROR_MESSAGE);
      } catch (e) {
        if (alive) setMessage(FALLBACK_ERROR_MESSAGE);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const groupOptions = Array.from(
    new Set(
      rows
        .map((r) => r.groupNo)
        .filter((g): g is number => g !== null && g !== undefined)
    )
  ).sort((a, b) => a - b);

  const emptyCount = rows.filter((r) => r.postCount === 0).length;
  const totalPosts = rows.reduce((sum, r) => sum + r.postCount, 0);

  const trimmedKeyword = keyword.trim().toLowerCase();
  const visibleRows = rows.filter((row) => {
    if (emptyOnly && row.postCount > 0) return false;
    if (
      !(
        groupFilter === GROUP_FILTER_ALL ||
        (groupFilter === GROUP_FILTER_NONE
          ? row.groupNo == null
          : String(row.groupNo) === groupFilter)
      )
    ) {
      return false;
    }
    if (trimmedKeyword.length === 0) return true;
    // 활동명과 게시물 주소 둘 다 훑는다 — "이 링크 누가 올렸지" 도 이 검색으로 답한다
    return [row.name ?? "", ...row.posts.map((p) => p.url)].some((value) =>
      value.toLowerCase().includes(trimmedKeyword)
    );
  });

  const sortedRows = (() => {
    if (!sort) return visibleRows;
    const value = POST_SORT_VALUE[sort.key];
    const sign = sort.dir === "asc" ? 1 : -1;
    // null 은 compareSortValue 가 항상 뒤로 보내므로 방향(sign)을 곱하지 않는다 —
    // 곱하면 내림차순에서 미등록(최근 등록 없음) 행이 맨 위를 채운다
    return [...visibleRows].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (av === null || bv === null) return compareSortValue(av, bv);
      return compareSortValue(av, bv) * sign;
    });
  })();

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  // 필터로 행이 줄면 현재 페이지가 범위를 벗어날 수 있다 — 표시 시점에 보정한다
  const currentPage = Math.min(page, totalPages);
  const pagedRows = sortedRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // 오름차순 → 내림차순 → 해제
  const toggleSort = (key: PostSortKey) => {
    setSort((prev) => {
      if (prev?.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
    setPage(1);
  };

  return (
    <section className="flex flex-col gap-4">
      <SectionHeader
        title="앰버서더 게시물"
        count={`${visibleRows.length}명 · 게시물 ${totalPosts}건`}
      >
        <Input
          value={keyword}
          placeholder="활동명, 게시물 링크 검색"
          fullWidth={false}
          className="w-64"
          onChange={(e) => {
            setKeyword(e.target.value);
            setPage(1);
          }}
        />
        <Select
          value={groupFilter}
          aria-label="모집 차수 필터"
          fullWidth={false}
          className="w-32"
          onChange={(e) => {
            setGroupFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value={GROUP_FILTER_ALL}>전체 차수</option>
          <option value={GROUP_FILTER_NONE}>미지정</option>
          {groupOptions.map((g) => (
            <option key={g} value={String(g)}>
              {groupNoLabel(g)}
            </option>
          ))}
        </Select>
        <Button
          variant={emptyOnly ? "primary" : "secondary"}
          disabled={emptyCount === 0 && !emptyOnly}
          onClick={() => {
            setEmptyOnly((v) => !v);
            setPage(1);
          }}
        >
          미등록 {emptyCount}
        </Button>
      </SectionHeader>

      {message && <ErrorBanner>{message}</ErrorBanner>}

      <Table>
        <THead>
          <SortTH
            label="차수"
            sortKey="groupNo"
            sort={sort}
            onSort={toggleSort}
          />
          <SortTH
            label="활동명"
            sortKey="name"
            sort={sort}
            onSort={toggleSort}
          />
          <SortTH
            label="진행"
            sortKey="progress"
            sort={sort}
            onSort={toggleSort}
          />
          <TH>게시물</TH>
          <SortTH
            label="최근 등록"
            sortKey="lastPostedDate"
            sort={sort}
            onSort={toggleSort}
          />
        </THead>
        <tbody>
          {loading && <TSkeleton colSpan={POST_COL_SPAN} />}
          {!loading && pagedRows.length === 0 && (
            <TEmpty colSpan={POST_COL_SPAN}>
              {rows.length === 0
                ? "링크를 만든 앰버서더가 없어요"
                : emptyOnly
                  ? "게시물을 추가하지 않은 앰버서더가 없어요"
                  : "조건에 맞는 앰버서더가 없어요"}
            </TEmpty>
          )}
          {pagedRows.map((row) => (
            <TRow
              key={row.ambassadorId}
              className={row.postCount === 0 ? "bg-amber-50/60" : ""}
            >
              <TD muted>
                {row.groupNo == null ? "-" : groupNoLabel(row.groupNo)}
              </TD>
              <TD className="font-medium">{row.name ?? "-"}</TD>
              <TD>
                {row.postCount >= row.requiredCount ? (
                  <Badge tone="ok">
                    {row.postCount} / {row.requiredCount}
                  </Badge>
                ) : (
                  <span className={NUMERIC}>
                    {row.postCount} / {row.requiredCount}
                  </span>
                )}
              </TD>
              <TD>
                {row.posts.length === 0 ? (
                  <span className="text-slate-400">-</span>
                ) : (
                  <div className="flex flex-col gap-1">
                    {row.posts.map((post) => {
                      const PlatformIcon =
                        POST_PLATFORM_ICON[post.platform] ?? LinkRoundedIcon;
                      return (
                        <div
                          key={post.postId}
                          className="flex items-center gap-2"
                        >
                          <PlatformIcon
                            titleAccess={post.platform}
                            className="shrink-0 text-slate-400"
                            sx={{ fontSize: 16 }}
                          />
                          <a
                            href={post.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="max-w-[420px] truncate text-[#0F55A7] hover:underline"
                            title={post.url}
                          >
                            {post.url}
                          </a>
                          <span
                            className={`shrink-0 text-xs text-slate-400 ${NUMERIC}`}
                          >
                            {post.createdDate ?? "-"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TD>
              <TD muted className={NUMERIC}>
                {row.lastPostedDate ?? "-"}
              </TD>
            </TRow>
          ))}
        </tbody>
      </Table>

      {/* 페이지 이동 — 한 페이지를 넘길 만큼 있을 때만 노출한다 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setPage(currentPage - 1)}
          >
            이전
          </Button>
          <span className="text-sm tabular-nums text-slate-500">
            {currentPage} / {totalPages}
          </span>
          <Button
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => setPage(currentPage + 1)}
          >
            다음
          </Button>
        </div>
      )}
    </section>
  );
}

const PERFORMANCE_COL_SPAN = 9;

function PerformanceSection() {
  const [preset, setPreset] = useState<PerformancePreset>("thisMonth");
  const [range, setRange] = useState<[string, string]>(() =>
    presetRange("thisMonth")
  );
  const [sort, setSort] = useState<PerformanceSortState>({
    key: "payerCount",
    desc: true,
  });
  const [overview, setOverview] =
    useState<ReferralAdminPerformanceOverviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [detailCode, setDetailCode] = useState<string | null>(null);

  const [from, to] = range;

  // silent: 자동 갱신 — 스켈레톤으로 갈아끼우지 않는다 (읽고 있던 값이 사라지면 안 된다).
  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        // 로컬은 서버 없이 화면을 확인할 수 있게 목 데이터로 채운다 (dev/real 은 실제 API)
        if (PERFORMANCE_MOCK_ENABLED) {
          setOverview(mockPerformanceOverview(from, to));
          setMessage(null);
          return;
        }
        const res = await adminApi.performanceOverview(from, to);
        if (res.code === 0 && res.result) {
          setOverview(res.result);
          setMessage(null);
        } else {
          setMessage(res.message || FALLBACK_ERROR_MESSAGE);
        }
      } catch (e) {
        setMessage(FALLBACK_ERROR_MESSAGE);
      } finally {
        setLoading(false);
      }
    },
    [from, to]
  );

  useEffect(() => {
    load();
  }, [load]);

  // 자동 갱신 — 다른 탭을 보는 동안에는 쉬고, 돌아오는 즉시 한 번 맞춘다.
  useEffect(() => {
    const tick = () => {
      if (!document.hidden) load(true);
    };
    const timer = window.setInterval(tick, PERFORMANCE_REFRESH_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [load]);

  const applyPreset = (next: PerformancePreset) => {
    setPreset(next);
    if (next !== "custom") setRange(presetRange(next));
  };

  const rows = useMemo(() => {
    return [...(overview?.rows ?? [])].sort((a, b) => {
      const left = performanceSortValue(a, sort.key);
      const right = performanceSortValue(b, sort.key);
      const order =
        typeof left === "string" && typeof right === "string"
          ? left.localeCompare(right)
          : Number(left) - Number(right);
      return sort.desc ? -order : order;
    });
  }, [overview, sort]);

  // 정산액은 수수료가 설정된 행만 합산하고, 하나도 없으면 미산출(null)로 남긴다.
  const totals = useMemo(() => {
    const settlements = rows
      .map((row) => row.metrics.settlementAmount)
      .filter((amount): amount is number => amount != null);
    return {
      signupCount: rows.reduce((sum, row) => sum + row.metrics.signupCount, 0),
      payerCount: rows.reduce((sum, row) => sum + row.metrics.payerCount, 0),
      refundCount: rows.reduce((sum, row) => sum + row.metrics.refundCount, 0),
      revenueAmount: rows.reduce(
        (sum, row) => sum + row.metrics.revenueAmount,
        0
      ),
      settlementAmount: settlements.length
        ? settlements.reduce((sum, amount) => sum + amount, 0)
        : null,
    };
  }, [rows]);

  const onSort = (key: PerformanceSortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, desc: !prev.desc }
        : { key, desc: key !== "name" }
    );
  };

  const detailRow = rows.find((row) => row.code === detailCode);

  return (
    <section className="flex flex-col gap-4">
      <SectionHeader
        title="앰버서더 성과"
        count={overview ? `${rows.length}개 링크` : undefined}
      >
        {PERFORMANCE_MOCK_ENABLED && <Badge tone="warn">목 데이터</Badge>}
        {/* 기간 프리셋 — 표 전체가 이 하나로 움직인다 */}
        <div className="flex rounded-md border border-slate-300 bg-white p-0.5">
          {PERFORMANCE_PRESETS.map((item) => (
            <button
              type="button"
              key={item.key}
              className={`rounded px-2.5 py-1 text-sm font-medium transition-colors ${
                preset === item.key
                  ? "bg-[#0F55A7] text-white"
                  : "text-slate-500 hover:text-slate-900"
              }`}
              onClick={() => applyPreset(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex items-center gap-1.5">
            <Input
              fullWidth={false}
              className="w-36"
              type="date"
              aria-label="집계 시작일"
              value={from}
              max={to}
              onChange={(e) => setRange([e.target.value, to])}
            />
            <span className="text-sm text-slate-400">~</span>
            <Input
              fullWidth={false}
              className="w-36"
              type="date"
              aria-label="집계 종료일"
              value={to}
              min={from}
              onChange={(e) => setRange([from, e.target.value])}
            />
          </div>
        )}
        {/* 새로고침은 아이콘만 — 기간 프리셋 옆에서 라벨까지 달면 시선을 뺏는다.
            테두리·배경도 없애고(ghost) 아이콘만 남긴다. 얇은 선 아이콘은 테두리가
            없어지면 묻히므로 채워진 MUI Refresh 를 20px·slate-700 으로 쓴다.
            제목은 툴팁·스크린리더로 남긴다. 불러오는 중에는 아이콘을 돌린다. */}
        <Button
          variant="ghost"
          size="sm"
          className="w-8 px-0 text-slate-700"
          aria-label="새로고침"
          title="새로고침"
          disabled={loading}
          onClick={() => load()}
        >
          <RefreshIcon
            className={loading ? "animate-spin" : ""}
            sx={{ fontSize: 20 }}
          />
        </Button>
      </SectionHeader>

      {message && <ErrorBanner>{message}</ErrorBanner>}

      {overview && <LiveStrip live={overview.live} />}

      <Table>
        <THead>
          <PerformanceSortTH
            label="앰버서더"
            sortKey="name"
            sort={sort}
            onSort={onSort}
          />
          <TH>유형</TH>
          <PerformanceSortTH
            numeric
            label="가입"
            sortKey="signupCount"
            sort={sort}
            onSort={onSort}
          />
          <PerformanceSortTH
            numeric
            label="결제"
            sortKey="payerCount"
            sort={sort}
            onSort={onSort}
          />
          <PerformanceSortTH
            numeric
            label="환불"
            sortKey="refundCount"
            sort={sort}
            onSort={onSort}
          />
          <PerformanceSortTH
            numeric
            label="매출"
            sortKey="revenueAmount"
            sort={sort}
            onSort={onSort}
          />
          {/* 계약서 제4조·앰버서더 안내와 같은 말 — 유효 결제 건당 지급액 */}
          <TH numeric>수수료</TH>
          <PerformanceSortTH
            numeric
            label="정산액"
            sortKey="settlementAmount"
            sort={sort}
            onSort={onSort}
          />
          <TH>최근 14일</TH>
        </THead>
        <tbody>
          {loading && !overview && <TSkeleton colSpan={PERFORMANCE_COL_SPAN} />}
          {!loading && rows.length === 0 && (
            <TEmpty colSpan={PERFORMANCE_COL_SPAN}>
              아직 발급된 앰버서더 링크가 없어요
            </TEmpty>
          )}
          {rows.map((row) => (
            <TRow key={row.code}>
              <TD>
                {/* 이름을 눌러 그 앰버서더의 일별·월별 추이를 연다 */}
                <CodeEditLink onClick={() => setDetailCode(row.code)}>
                  <span className="font-medium">{row.name ?? row.code}</span>
                  <span className="ml-1.5 text-xs text-slate-400">
                    {row.code}
                  </span>
                </CodeEditLink>
              </TD>
              <TD muted>{ambassadorLabel(row.groupNo)}</TD>
              <TD numeric>{row.metrics.signupCount}</TD>
              <TD numeric className="font-medium">
                {row.metrics.payerCount}
              </TD>
              <TD numeric muted>
                {row.metrics.refundCount}
              </TD>
              <TD numeric>{row.metrics.revenueAmount.toLocaleString()}원</TD>
              <TD numeric muted>
                {wonLabel(row.perPayerAmount)}
              </TD>
              <TD numeric className="font-semibold">
                {wonLabel(row.metrics.settlementAmount)}
              </TD>
              <TD>
                <Sparkline values={row.trend} />
              </TD>
            </TRow>
          ))}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr className="whitespace-nowrap border-t border-slate-200 bg-slate-50 font-semibold">
              <TD>합계</TD>
              <TD />
              <TD numeric>{totals.signupCount}</TD>
              <TD numeric>{totals.payerCount}</TD>
              <TD numeric>{totals.refundCount}</TD>
              <TD numeric>{totals.revenueAmount.toLocaleString()}원</TD>
              <TD />
              <TD numeric>{wonLabel(totals.settlementAmount)}</TD>
              <TD />
            </tr>
          </tfoot>
        )}
      </Table>

      {/* 각주는 왼쪽 정렬 — 정의를 모르면 전환율·정산액을 잘못 읽는다.
          갱신 시각은 표 오른쪽 끝에 붙여 "지금 값인지"를 바로 알 수 있게 둔다. */}
      <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-2">
        <p className="max-w-[70ch] text-xs leading-relaxed text-slate-400">
          기여 기반인 앰버서더 링크만 집계해요 (할인 링크는 기여를 기록하지 않아
          성과 대상이 아니에요). 가입 = 링크로 들어와 로그인한 회원(링크 클릭
          자체는 집계되지 않아요) · 결제 = 정산 기여가 확정된 회원(1인 1회) ·
          매출 = 그 기여 결제의 결제액(환불·갱신 결제 제외). 기여는 마지막 링크
          진입 후 3일 안의 결제면 인정돼요 — 할인 기간(결제페이지 진입 후
          24시간)이 지나 정가로 결제한 건도 포함돼요. 결제·환불·정산액은
          앰버서더 본인 대시보드·정산 탭과 같은 기준이고, 정산액은 예상치이며
          지급 기준은 정산 탭이에요.
        </p>
        {overview && (
          <p className="ml-auto text-xs text-slate-400">
            {new Date(overview.live.asOf).toLocaleTimeString("ko-KR", {
              hour: "2-digit",
              minute: "2-digit",
            })}{" "}
            기준 · {PERFORMANCE_REFRESH_LABEL}
          </p>
        )}
      </div>

      {detailCode && (
        <PerformanceDetailModal
          code={detailCode}
          name={detailRow?.name ?? detailCode}
          onClose={() => setDetailCode(null)}
        />
      )}
    </section>
  );
}

// 앰버서더 1명의 전체 기간 추이 — 개요 표에서 이름을 눌러 연다.
// 개요와 달리 기간 필터가 없다 (이 코드가 언제 움직였는지를 통째로 보는 자리).
function PerformanceDetailModal({
  code,
  name,
  onClose,
}: {
  code: string;
  name: string;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<"daily" | "monthly">("daily");
  const [result, setResult] = useState<AmbassadorDashboardResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (PERFORMANCE_MOCK_ENABLED) {
          setResult(mockPerformanceDetail(code));
          return;
        }
        const res = await adminApi.performance(code);
        if (res.code === 0 && res.result) setResult(res.result);
        else setMessage(res.message || FALLBACK_ERROR_MESSAGE);
      } catch (e) {
        setMessage(FALLBACK_ERROR_MESSAGE);
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  const buckets = (tab === "daily" ? result?.daily : result?.monthly) ?? [];
  const colSpan = tab === "monthly" ? 5 : 4;

  return (
    <Modal
      title="앰버서더 성과"
      subject={`${name} · ${code}`}
      width="640px"
      onClose={onClose}
    >
      {message && <ErrorBanner>{message}</ErrorBanner>}

      <div className="mb-3 flex items-center justify-between gap-3">
        {result && (
          <p className="text-sm text-slate-500">
            전체 기간 가입{" "}
            <span className="font-semibold tabular-nums text-slate-900">
              {result.total.signupCount}
            </span>{" "}
            · 결제{" "}
            <span className="font-semibold tabular-nums text-slate-900">
              {result.total.payerCount}
            </span>{" "}
            · 환불{" "}
            <span className="font-semibold tabular-nums text-slate-900">
              {result.total.refundCount}
            </span>
          </p>
        )}
        {/* 월별/일별 전환 — 같은 표의 집계 단위만 바꾼다 */}
        <div className="ml-auto flex rounded-md border border-slate-300 bg-white p-0.5">
          {(["daily", "monthly"] as const).map((t) => (
            <button
              type="button"
              key={t}
              className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
                tab === t
                  ? "bg-[#0F55A7] text-white"
                  : "text-slate-500 hover:text-slate-900"
              }`}
              onClick={() => setTab(t)}
            >
              {t === "monthly" ? "월별" : "일별"}
            </button>
          ))}
        </div>
      </div>

      <Table>
        <THead>
          <TH>{tab === "daily" ? "날짜" : "월"}</TH>
          <TH numeric>가입자</TH>
          <TH numeric>결제자</TH>
          <TH numeric>환불자</TH>
          {tab === "monthly" && <TH numeric>정산액</TH>}
        </THead>
        <tbody>
          {loading && <TSkeleton colSpan={colSpan} />}
          {!loading && buckets.length === 0 && (
            <TEmpty colSpan={colSpan}>아직 집계된 데이터가 없어요</TEmpty>
          )}
          {!loading &&
            buckets.map((bucket) => (
              <TRow key={bucket.period}>
                <TD>{bucket.period}</TD>
                <TD numeric>{bucket.signupCount}</TD>
                <TD numeric>{bucket.payerCount}</TD>
                <TD numeric>{bucket.refundCount}</TD>
                {tab === "monthly" && (
                  <TD numeric>{wonLabel(bucket.settlementAmount)}</TD>
                )}
              </TRow>
            ))}
        </tbody>
      </Table>
    </Modal>
  );
}

// ---------- 월별 정산 ----------

// 계약형태별 실지급액 (계약서 제4조).
// - 프리랜서: 수수료는 "원천징수 전" 금액이고 소득세·지방소득세 3.3% 를 뗀 잔액을 지급한다.
// - 사업자: 수수료는 "부가가치세 별도" 라 10% 를 더해 지급하고 크리에이터가 세금계산서를 발행한다.
// 원 단위 절사 — 참고 계산이고 실제 이체 금액은 세무 처리 기준을 따른다.
const payoutBreakdown = (amount: number, contractType?: string | null) => {
  if (contractType === "freelancer") {
    const withholding = Math.floor(amount * 0.033);
    return {
      label: "원천징수 (3.3%)",
      adjustment: -withholding,
      payout: amount - withholding,
      note: "소득세·지방소득세를 뗀 잔액을 지급해요.",
    };
  }
  const vat = Math.floor(amount * 0.1);
  return {
    label: "부가가치세 (10%)",
    adjustment: vat,
    payout: amount + vat,
    note: "부가세를 더해 지급하고, 크리에이터가 세금계산서를 발행해요.",
  };
};

// 지급 정보 팝업 — 실제로 돈을 보낼 때 필요한 값을 한 화면에 모은다.
// 계좌번호는 민감정보라 목록 응답에 싣지 않고 여기서만 상세로 가져온다.
function PayoutModal({
  row,
  month,
  onClose,
}: {
  row: ReferralAdminSettlementRow;
  month: string;
  onClose: () => void;
}) {
  const [detail, setDetail] =
    useState<ReferralAdminAmbassadorContractDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      if (row.ambassadorId == null) return;
      try {
        const res = await adminApi.ambassadorContract(row.ambassadorId);
        if (res.code === 0 && res.result) setDetail(res.result);
        else setMessage(res.message || FALLBACK_ERROR_MESSAGE);
      } catch (e) {
        setMessage(FALLBACK_ERROR_MESSAGE);
      } finally {
        setLoading(false);
      }
    })();
  }, [row.ambassadorId]);

  const amount = row.settlementAmount ?? 0;
  const breakdown = payoutBreakdown(amount, detail?.contractType);

  return (
    <Modal
      title="지급 정보"
      subject={`${row.name ?? row.code} · ${month}`}
      width="480px"
      onClose={onClose}
    >
      {message && <ErrorBanner>{message}</ErrorBanner>}
      {loading && <p className="text-sm text-slate-400">불러오는 중…</p>}

      {!loading && detail && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <InfoRow label="계약형태">
              {contractTypeLabel(detail.contractType)}
            </InfoRow>
            <InfoRow label="예금주">
              {detail.accountHolderName ?? "미제출"}
              {detail.accountHolderMismatch && (
                // 예금주가 계약 명의와 다르면 잘못 보낼 수 있다 — 이체 직전에 알려야 하는 경고다
                <span className="ml-2 text-xs font-medium text-rose-600">
                  계약 명의와 달라요 · 확인 필요
                </span>
              )}
            </InfoRow>
            <InfoRow label="은행">{detail.bankName ?? "미제출"}</InfoRow>
            <InfoRow label="계좌번호">{detail.bankAccount ?? "미제출"}</InfoRow>
          </div>

          {/* 금액 — 계약형태에 따라 실지급액이 다르다 (제4조) */}
          <div className="flex flex-col gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">정산액</span>
              <span className="tabular-nums text-slate-900">
                {amount.toLocaleString()}원
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">{breakdown.label}</span>
              <span className="tabular-nums text-slate-900">
                {breakdown.adjustment < 0 ? "−" : "+"}
                {Math.abs(breakdown.adjustment).toLocaleString()}원
              </span>
            </div>
            <div className="mt-1 flex justify-between border-t border-slate-200 pt-2">
              <span className="font-medium text-slate-900">실지급액</span>
              <span className="text-base font-semibold tabular-nums text-slate-900">
                {breakdown.payout.toLocaleString()}원
              </span>
            </div>
          </div>

          <p className="text-xs leading-relaxed text-slate-400">
            {breakdown.note} 실지급액은 참고 계산이고, 최종 금액은 세무 처리
            기준을 따라요.
            {!row.accountHolderName &&
              " 정산 서류가 아직 제출되지 않아 지급할 수 없어요."}
          </p>
        </div>
      )}
    </Modal>
  );
}

function SettlementSection() {
  const [month, setMonth] = useState(currentMonth());
  const [result, setResult] = useState<ReferralAdminSettlementResult | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  // 지급 정보 팝업 — 계좌번호는 목록 응답에 싣지 않고 누를 때만 상세로 가져온다
  const [payoutRow, setPayoutRow] = useState<ReferralAdminSettlementRow | null>(
    null
  );

  const load = async (targetMonth: string) => {
    setMonth(targetMonth);
    setResult(null);
    setMessage(null);
    if (!targetMonth) return;
    // min 은 달력 UI만 막는다 — 직접 입력한 이전 달은 여기서 걸러 빈 표 대신 이유를 보여준다.
    if (targetMonth < SETTLEMENT_MIN_MONTH) {
      setMessage(`${SETTLEMENT_MIN_MONTH} 이전은 정산 기록이 없어요.`);
      return;
    }
    setLoading(true);
    try {
      const res = await adminApi.settlement(targetMonth);
      if (res.code === 0 && res.result) setResult(res.result);
      else setMessage(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(currentMonth());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="flex flex-col gap-4">
      <SectionHeader title="월별 정산">
        {result &&
          (result.confirmed ? (
            <Badge tone="ok">확정</Badge>
          ) : (
            <Badge tone="warn">예상</Badge>
          ))}
        {/* 월 이동 — 셀렉터로도 고를 수 있지만 한 달씩 넘기는 게 더 잦다.
            테두리·배경 없이 아이콘만 둔다(ghost) — 헤더에서 월 입력보다 시선을 끌면 안 된다.
            범위는 화살표와 셀렉터가 같은 값을 쓴다(min/max) — 한쪽만 막히면 어긋나 보인다.
            불러오는 중에는 잠근다: 연타하면 응답이 엇갈려 다른 달의 표가 남을 수 있다. */}
        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            className="w-8 px-0 text-slate-700"
            aria-label="이전 달"
            title="이전 달"
            disabled={loading || month <= SETTLEMENT_MIN_MONTH}
            onClick={() => load(shiftMonth(month, -1))}
          >
            <KeyboardArrowLeftRoundedIcon sx={{ fontSize: 20 }} />
          </Button>
          <Input
            fullWidth={false}
            className="w-40"
            type="month"
            aria-label="정산 월"
            value={month}
            min={SETTLEMENT_MIN_MONTH}
            max={currentMonth()}
            onChange={(e) => load(e.target.value)}
          />
          <Button
            variant="ghost"
            size="sm"
            className="w-8 px-0 text-slate-700"
            aria-label="다음 달"
            title="다음 달"
            disabled={loading || month === "" || month >= currentMonth()}
            onClick={() => load(shiftMonth(month, 1))}
          >
            <KeyboardArrowRightRoundedIcon sx={{ fontSize: 20 }} />
          </Button>
        </div>
      </SectionHeader>

      {/* 이 표가 "지금 계산한 예상치"인지 "지급 근거로 굳은 값"인지를 표 위에서 먼저 알려준다.
          확정 전 숫자를 보고 지급하면 그 뒤 환불로 금액이 달라진다. */}
      {result && (
        <p
          className={`rounded-md border px-3 py-2 text-sm leading-relaxed ${
            result.confirmed
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-amber-200 bg-amber-50 text-amber-900"
          }`}
        >
          {result.confirmed ? (
            <>
              {formatDateTime(result.confirmedAt)}에 확정된 금액이에요. 이후
              환불이 나거나 수수료를 바꿔도 바뀌지 않아요.
              {result.payoutDate && ` 지급일은 ${result.payoutDate} 이에요.`}
            </>
          ) : (
            <>
              아직 확정 안된 예상치예요. {confirmDateOf(month)}에 확정되고,
              그때까지 환불이 나면 금액이 달라질 수 있어요.
            </>
          )}
        </p>
      )}

      {message && <ErrorBanner>{message}</ErrorBanner>}

      {payoutRow && (
        <PayoutModal
          row={payoutRow}
          month={month}
          onClose={() => setPayoutRow(null)}
        />
      )}

      {(loading || result) && (
        <>
          <Table>
            <THead>
              <TH>이름</TH>
              <TH>코드</TH>
              <TH>입금자명</TH>
              {/* 결제 − 7일 내 환불 = 유효 결제 × 수수료 = 정산액 이 좌→우로 이어지게 둔다.
                  "7일 내" 를 헤더에 박는 이유: 이 열은 환불 건수가 아니라 정산에서 빠진 건수다.
                  창 밖(7일 뒤) 환불은 여기에 안 잡히므로 그냥 "환불" 이면 0으로 오독된다.
                  확정 전후로 뜻이 같은 열이어야 한다 — 확정 스냅샷도 같은 둘을 담는다 */}
              <TH numeric>결제</TH>
              <TH numeric>7일 내 환불</TH>
              <TH numeric>유효 결제</TH>
              <TH numeric>수수료</TH>
              <TH numeric>정산액</TH>
            </THead>
            <tbody>
              {loading && <TSkeleton colSpan={8} />}
              {!loading && result?.rows.length === 0 && (
                <TEmpty colSpan={8}>이 달에는 정산할 활동이 없어요</TEmpty>
              )}
              {!loading &&
                result?.rows.map((row) => (
                  <TRow key={row.code}>
                    <TD>{row.name ?? "-"}</TD>
                    <TD className="font-medium">{row.code}</TD>
                    {/* 정산 서류를 안 낸 앰버서더는 예금주가 없다 — 지급할 수 없다는 뜻이라
                        빈칸이 아니라 경고 톤으로 눈에 띄게 둔다.
                        눌러서 계좌·실지급액 등 이체에 필요한 정보를 한 번에 본다. */}
                    <TD
                      className={row.accountHolderName ? "" : "text-rose-600"}
                    >
                      {row.ambassadorId != null ? (
                        <CodeEditLink onClick={() => setPayoutRow(row)}>
                          {row.accountHolderName ?? "미제출"}
                        </CodeEditLink>
                      ) : (
                        (row.accountHolderName ?? "-")
                      )}
                    </TD>
                    {/* 결제 = 유효 + 제외 — 서버 countValidPayments 가 그 달 기여 결제를
                        이 둘로만 나눈다 (AmbassadorBO.countValidPayments) */}
                    <TD numeric>{row.payerCount + row.refundCount}</TD>
                    <TD numeric>{row.refundCount}</TD>
                    <TD numeric>{row.payerCount}</TD>
                    <TD numeric>
                      {row.perPayerAmount != null
                        ? `${row.perPayerAmount.toLocaleString()}원`
                        : "-"}
                    </TD>
                    <TD numeric className="font-semibold">
                      {row.settlementAmount != null
                        ? `${row.settlementAmount.toLocaleString()}원`
                        : "-"}
                    </TD>
                  </TRow>
                ))}
            </tbody>
          </Table>

          {result && (
            // 각주는 왼쪽 정렬 — 여러 줄을 우측 정렬하면 왼쪽 끝이 들쭉날쭉해 읽기 어렵다.
            // 합계는 표의 정산액 열과 같은 오른쪽 끝에 맞춘다.
            <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-2">
              <p className="max-w-[60ch] text-xs leading-relaxed text-slate-400">
                유효 결제 = 결제 − 7일 내 환불, 정산액 = 유효 결제 × 해당 월
                적용 수수료예요 (계약서 제4조). 결제일로부터 7일이 지난 뒤의
                환불은 차감하지 않아서 이 표의 환불에도 안 잡혀요.
                {result.confirmed
                  ? ""
                  : " 확정 전에는 환불 창이 아직 안 닫힌 건도 유효로 세므로 금액이 줄어들 수 있어요."}
              </p>
              <p className="ml-auto text-sm text-slate-500">
                합계{" "}
                <span className="text-base font-semibold tabular-nums text-slate-900">
                  {result.totalAmount.toLocaleString()}원
                </span>
              </p>
            </div>
          )}
        </>
      )}
    </section>
  );
}
